import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AdminContractReview from "../components/AdminContractReview";
import AdminChangeRequests from "../components/AdminChangeRequests";
import AdminCancellations from "../components/AdminCancellations";
import SupportTickets from "../components/SupportTickets";
import PayoutDetailModal from "../components/PayoutDetailModal";
import RecipientOverviewModal from "../components/RecipientOverviewModal";
import AdminContractDetailModal from "../components/AdminContractDetailModal";
import CreditHistoryModal from "../components/CreditHistoryModal";
import TopupDetailModal from "../components/TopupDetailModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { weightedFor, hasCasinoSelfReported } from "../lib/wagerWeighted";

function Pill({ tone = "default", children }) {
  const tones = { default: "border-white/20 text-white", warn: "border-[#F5B841]/50 text-[#F5B841]", ok: "border-emerald-500/50 text-emerald-400", danger: "border-[#F26D6D]/60 text-[#F26D6D]" };
  return <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${tones[tone]}`}>{children}</span>;
}

export default function AdminDashboard() {
  const { t } = useI18n();
  const [stats, setStats] = useState({});
  const [players, setPlayers] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [casinos, setCasinos] = useState([]);
  const [tiersCfg, setTiersCfg] = useState({});                 // {casino: cfg, case_opening: cfg}
  // Iter 79 — Controlled Tabs. `activeTab` also drives the per-tab scope
  // filter (pending vs active/done) for players/streamers/topups/payouts.
  const [activeTab, setActiveTab] = useState("players-pending");
  const [tiersVertical, setTiersVertical] = useState("casino");
  const [tiersDraft, setTiersDraft] = useState({ minVolume: "", rate: "" });
  const [tiersBulkText, setTiersBulkText] = useState("");
  const [tiersBulkPreview, setTiersBulkPreview] = useState(null); // array of {minVolume, rate} or {error}
  const [tiersBusy, setTiersBusy] = useState(false);
  // Iter 80 — Payout detail modal shared between Pending + Done tabs.
  // Iter 134 — Reset environment (Danger Zone).
  const [resetConfirm, setResetConfirm] = useState("");
  // Iter 135 — reset password (compared server-side against RESET_PASSWORD_HASH).
  // Not stored, not autofilled, not echoed anywhere. Type="password".
  const [resetPassword, setResetPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  // CMLD — "Run maintenance now" admin button. Fires POST
  // /api/admin/cmld/maintenance which runs the FULL maintenance body
  // (archive + reconcile + stuck-reserve) and returns the three result
  // lines. Admin-only; audited server-side as cmld.maintenance.manual.
  const [maintBusy, setMaintBusy] = useState(false);
  const [maintResult, setMaintResult] = useState(null);
  const runMaintenance = async () => {
    if (maintBusy) return;
    setMaintBusy(true); setMaintResult(null);
    try {
      const r = await api.post("/admin/cmld/maintenance", {});
      setMaintResult(r.data);
      toast.success("Maintenance complete");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Maintenance failed");
      setMaintResult({ error: apiErrorMessage(e) || "failed" });
    } finally {
      setMaintBusy(false);
    }
  };
  const runReset = async () => {
    if (resetConfirm.trim() !== "RESET") return;
    if (!resetPassword) { toast.error("Reset password is required"); return; }
    if (!window.confirm("Irreversible: wipe ALL test content? Admin accounts + config are kept.")) return;
    setResetBusy(true); setResetResult(null);
    try {
      const { data } = await api.post("/admin/system/reset-environment", {
        confirmation: "RESET",
        password: resetPassword,
      });
      setResetResult(data);
      toast.success("Environment reset complete");
      setResetConfirm("");
      setResetPassword("");
      await load(); await loadIter3(); await loadCounts();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setResetBusy(false); }
  };

  // Iter 179 — Manual "Send wager-verification reminder now" button.
  // Reuses the SAME sweep endpoint the daily 16:00 UTC cron hits — this
  // is just a UI trigger, no new backend logic. Two-step: dry-run first
  // to fetch the matched count, then a window.confirm before firing the
  // real send. Real-send response drives the result toast.
  const [wagerReminderBusy, setWagerReminderBusy] = useState(false);
  const [wagerReminderResult, setWagerReminderResult] = useState(null);
  const runWagerReminder = async () => {
    setWagerReminderBusy(true);
    setWagerReminderResult(null);
    try {
      const { data: dry } = await api.post("/admin/wager-reminder/run?dry_run=true");
      const matched = Number(dry?.matched ?? 0);
      if (matched === 0) {
        toast.info("No players currently due for a reminder.");
        setWagerReminderResult({ matched: 0, sent: 0, failed: 0, dry_run: true });
        return;
      }
      const ok = window.confirm(
        `This will send the wager verification reminder to ${matched} player(s) who haven't received it yet. Send now?`
      );
      if (!ok) return;
      const { data: real } = await api.post("/admin/wager-reminder/run?dry_run=false");
      setWagerReminderResult(real);
      const sent = Number(real?.sent ?? 0);
      const failed = Number(real?.failed ?? 0);
      if (failed > 0) {
        toast.warning(`Reminder sent to ${sent} · ${failed} failed. Check server logs.`);
      } else {
        toast.success(`Reminder sent to ${sent} player(s).`);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWagerReminderBusy(false);
    }
  };

  const [openPayoutId, setOpenPayoutId] = useState(null);
  // Iter 180 — per-player wager-reminder send state, keyed by playerId
  // so a mid-flight click on one player never masks another's UI.
  const [playerReminderBusy, setPlayerReminderBusy] = useState({});
  // Iter 192 — per-player state for the Request-More-Info block. Keyed by
  // playerId so switching between detail modals never leaks the previous
  // textarea contents or busy state.
  const [moreInfoMsg, setMoreInfoMsg] = useState({});
  const [moreInfoBusy, setMoreInfoBusy] = useState({});
  // Iter 133 — Compact admin recipient overview opened by clicking the
  // recipient name in the Payouts Pending list.
  const [openRecipientForPayoutId, setOpenRecipientForPayoutId] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  // Iter 82 (UI) — Contract Iter 3 admin list + detail modal.
  const [iter3Contracts, setIter3Contracts] = useState([]);
  const [openContractId, setOpenContractId] = useState(null);
  const [contractsBusy, setContractsBusy] = useState(false);
  // Iter 130 — Admin contracts state-group filter. Buckets cover every
  // contract state so nothing is unfindable. `all` shows every row.
  const [contractStatusFilter, setContractStatusFilter] = useState("all");
  // Iter 131 — free-text search over player alias, streamer alias, and
  // contract id. Combines with the status chip (chip narrows to bucket,
  // search narrows within it). Client-side over the already-fetched list.
  const [contractSearch, setContractSearch] = useState("");
  const loadIter3 = async () => {
    try { const r = await api.get("/admin/contracts?flow=iter3"); setIter3Contracts(r.data || []); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { loadIter3(); }, []);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [topups, setTopups] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [cfg, setCfg] = useState({});
  const [receivingWallets, setReceivingWallets] = useState([]);
  const [newCasino, setNewCasino] = useState({ name: "", notes: "", active: true });
  const [newRef, setNewRef] = useState({ playerId: "", streamerId: "", casinoId: "", streamerCode: "", signupDate: new Date().toISOString().slice(0, 10), wasExistingCustomer: false, referenceVolume: 0, underCodeWagerStart: 0, committedVolume: 0 });
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [bulkSel, setBulkSel] = useState({});                 // {playerId: true}
  const [bulkBusy, setBulkBusy] = useState(false);
  // Persist skip reasons across page reloads so unresolved rows stay visible.
  // Lazy state init reads localStorage BEFORE any effect runs — this
  // eliminates the mount race between hydration and persist effects.
  const SKIP_KEY = "vg_admin_skip_reasons";
  const [skipReasons, setSkipReasons] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(SKIP_KEY) || "{}") || {}; }
    catch (e) { return {}; }
  });
  const [refVolDraft, setRefVolDraft] = useState({});         // {playerId: string} — inline edit buffer
  const [refVolBusy, setRefVolBusy] = useState({});           // {playerId: true} while saving
  // Iter 193 — admin-side reporting period/mode/total per (playerId, vertical).
  // Defaults 90/categorized/0 keep the pre-Iter-193 admin behaviour byte-identical
  // when untouched (backend applies scale 1.0 and the categorized formula).
  const [refVolPeriod, setRefVolPeriod] = useState({});       // {`${pid}:${vertical}`: 30 | 90}
  const [refVolMode,   setRefVolMode]   = useState({});       // {`${pid}:${vertical}`: "categorized" | "total_only"}
  const [refVolTotal,  setRefVolTotal]  = useState({});       // {`${pid}:${vertical}`: number}
  // Player per-vertical verification UI state — iteration 19.
  const [pvvBusy, setPvvBusy] = useState({});                 // {`${pid}:${vertical}`: true}
  const [pvvHistory, setPvvHistory] = useState(null);         // {player, rows} | null
  // Per-vertical enablement UI state — filter + async toggle guards + history modal.
  const [enablementFilter, setEnablementFilter] = useState("all"); // all | casino | case_opening | neither
  const [enablementBusy, setEnablementBusy] = useState({});   // {`${sid}:${vertical}`: true}
  const [enablementHistory, setEnablementHistory] = useState(null); // {streamer, rows} | null
  // Iter 57 -- streamer delete modal state. Two-step confirm: admin types the
  // streamer's email; backend enforces the 409 guard for live contracts.
  const [deleteOpen, setDeleteOpen] = useState(null);          // {streamer} | null
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Iter 39 — Streamer verification-review view state.
  const [verificationFilter, setVerificationFilter] = useState("all"); // all | pending | verified | rejected | needs_more_info — iter 57: default "all" so no account is ever hidden from the admin view
  // Iter 62 — server-side admin search across the full dataset (bypasses the
  // 1000-record list cap). Empty q => use the local list; ≥2 chars => the
  // source list is replaced by /streamers/search results, which still flow
  // through the existing verification/enablement filter + sort.
  const [streamerSearch, setStreamerSearch] = useState("");
  const [streamerSearchResults, setStreamerSearchResults] = useState(null);
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerSearchResults, setPlayerSearchResults] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);          // {streamer, proofUrls:{path:url}} | null
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [proofUrls, setProofUrls] = useState({});              // {path:url} for the currently-open review
  // Iter 61 — manual credit grant/remove on the streamer detail (review) modal.
  // Delta-based writes (never absolute); reason is required; a would-be-negative
  // available balance requires an explicit confirm — see backend guard.
  const [creditDelta, setCreditDelta] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [creditGrantType, setCreditGrantType] = useState("adjustment"); // Iter 147
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditHistoryStreamer, setCreditHistoryStreamer] = useState(null);
  const [sortBy, setSortBy] = useState("registered");          // registered | name | credits
  const [sortDir, setSortDir] = useState("desc");              // asc | desc
  // Iter 41 — contract overview state
  const [refsGroup, setRefsGroup] = useState("active");        // active | past | flagged
  const [reviewRefs, setReviewRefs] = useState([]);            // referrals scoped to the currently-open streamer
  // Iter 42 — Admin Player Detail modal (all PII lives here, never in the table).
  const [playerDetail, setPlayerDetail] = useState(null);      // full player doc from GET /players/{id}
  const [playerDetailBusy, setPlayerDetailBusy] = useState(false);
  const [playerProofUrls, setPlayerProofUrls] = useState({});  // {path:url} for the currently-open player
  // Iter 43 — Players table: rename Alias→Name (sortable), add Registered
  // (sortable), replace inline Ref-Vol editor with a display-only Weighted
  // wager column (casino + case). Reference-only compliance: NEVER writes to
  // billing/rebate/pricing fields; Baustein-3 (admin-confirmed weighted
  // monthly base) remains the sole billing basis.
  const [playerSortBy, setPlayerSortBy] = useState("registered"); // name | registered
  const [playerSortDir, setPlayerSortDir] = useState("desc");     // asc | desc
  // Iter 59 — Player filter bar. Composable with sort; default "all" so no
  // player is ever hidden by default (mirrors the streamers filter after
  // iter 57 fixed the disappearing-record bug).
  const [playerStatusFilter, setPlayerStatusFilter]   = useState("all"); // all | pending | approved
  const [playerCasinoFilter, setPlayerCasinoFilter]   = useState("all"); // all | verified | declined | pending
  const [playerCaseFilter, setPlayerCaseFilter]       = useState("all"); // all | verified | declined | pending
  const [playerReadyOnly, setPlayerReadyOnly]         = useState(false); // "ready to confirm" quick filter
  const [wagerCoeffCfg, setWagerCoeffCfg] = useState(null);       // {categories:[{key,coefficient}]}
  useEffect(() => {
    try { window.localStorage.setItem(SKIP_KEY, JSON.stringify(skipReasons)); } catch (e) { /* quota */ }
  }, [skipReasons]);
  // Whenever the players list refreshes, drop chip entries for players who
  // have since been approved or removed from the pending set.
  useEffect(() => {
    if (players.length === 0) return;                         // Don't prune before the first load resolves.
    setSkipReasons((old) => {
      const next = {};
      let changed = false;
      const activeIds = new Set(players.filter((p) => p.status !== "approved").map((p) => p.id));
      Object.keys(old).forEach((k) => {
        if (activeIds.has(k)) { next[k] = old[k]; } else { changed = true; }
      });
      return changed ? next : old;
    });
  }, [players]);

  // Only pending, email-verified players with an admin-set referenceVolume > 0
  // are truly "bulk-approvable". Everyone else stays checkable but the server
  // will skip them — we mirror the same guard client-side just for clarity.
  const eligibleIds = useMemo(
    () => players.filter((p) => p.status === "pending" && p.emailVerified && Number(p.referenceVolume || 0) > 0).map((p) => p.id),
    [players],
  );
  const selectedCount = Object.values(bulkSel).filter(Boolean).length;
  const selectedIds = Object.keys(bulkSel).filter((k) => bulkSel[k]);
  const anyEligibleSelected = selectedIds.some((id) => eligibleIds.includes(id));

  const toggleAll = () => {
    if (eligibleIds.every((id) => bulkSel[id])) {
      setBulkSel({});
    } else {
      const next = {};
      eligibleIds.forEach((id) => { next[id] = true; });
      setBulkSel(next);
    }
  };

  const bulkApprove = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one player"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/players/bulk-approve", { playerIds: selectedIds });
      const parts = [`${data.approvedCount} approved`];
      if (data.skippedCount) parts.push(`${data.skippedCount} skipped`);
      if (data.skippedCount) {
        toast.success(parts.join(" · "), { description: "See the gold chips below each skipped row for the exact reason." });
      } else {
        toast.success(parts.join(" · "));
      }
      // Persist skip reasons so the admin can see them in the table (chip beside alias).
      const nextReasons = { ...skipReasons };
      // Clear any reason for players that just got approved.
      data.approved.forEach((a) => { delete nextReasons[a.id]; });
      // Store latest reason for anything skipped this call.
      (data.skipped || []).forEach((s) => { nextReasons[s.id] = s.reason; });
      setSkipReasons(nextReasons);
      setBulkSel({});
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const saveRefVol = async (pid, vertical = "casino") => {
    const draftKey = vertical === "case_opening" ? `${pid}:case` : pid;
    const raw = refVolDraft[draftKey];
    const val = Number(raw);
    if (!Number.isFinite(val) || val < 0) { toast.error("Must be a non-negative number"); return; }
    setRefVolBusy((old) => ({ ...old, [draftKey]: true }));
    try {
      // Iter 54 — per-vertical write. Legacy scalar still mirrored server-side.
      // This value: (a) gates whether the player appears in that vertical's
      // marketplace (> 0), and (b) drives the unlock price for that vertical.
      // NEVER used in billing/rebate math.
      const patch = vertical === "case_opening" ? { referenceVolumeCase: val } : { referenceVolumeCasino: val };
      await api.patch(`/players/${pid}/admin`, patch);
      toast.success(`Reference volume saved (${vertical === "case_opening" ? "Case" : "Casino"})`);
      setSkipReasons((old) => {
        const next = { ...old };
        if (next[pid] === "no_reference_volume") delete next[pid];
        return next;
      });
      setRefVolDraft((old) => { const n = { ...old }; delete n[draftKey]; return n; });
      if (playerDetail && playerDetail.id === pid) {
        try { const { data } = await api.get(`/players/${pid}`); setPlayerDetail(data); } catch (_) {}
      }
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setRefVolBusy((old) => { const n = { ...old }; delete n[draftKey]; return n; });
    }
  };

  // Iter 113 — Aggregated live counts for each admin nav tab. One admin-gated
  // endpoint (`/admin/dashboard-counts`) returns integer counts only — no PII.
  // Filters mirror each tab's own list; is_test rows are excluded from
  // contracts/payouts counts so preview seed data never inflates the badges.
  const [tabCounts, setTabCounts] = useState(null);
  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get("/admin/dashboard-counts"); setTabCounts(data || {}); }
    catch (e) { /* soft-fail: labels just render without numbers */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, p, st, c, tr, r, po, tp, au, cf, rw] = await Promise.all([
        api.get("/admin/stats"), api.get("/players"), api.get("/streamers"),
        api.get("/casinos"), api.get("/config/commission-tiers"), api.get("/referrals"),
        api.get("/payouts"), api.get("/credit-topups"), api.get("/admin/audit-log?limit=200"), api.get("/config"),
        api.get("/admin/receiving-wallets"),
      ]);
      setStats(s.data); setPlayers(p.data); setStreamers(st.data); setCasinos(c.data);
      setTiersCfg(tr.data); setRefs(r.data); setPayouts(po.data); setTopups(tp.data); setAuditEntries(au.data); setCfg(cf.data);
      setReceivingWallets(rw.data?.wallets || []);
      loadCounts();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [loadCounts]);

  useEffect(() => { load(); }, [load]);

  // Iter 113 — Refresh counts when the admin switches tabs, and whenever
  // the window regains focus (subtle, no polling). Every existing post-action
  // refresh flows through load() above, which also re-fetches counts.
  useEffect(() => { loadCounts(); }, [activeTab, loadCounts]);
  useEffect(() => {
    const onFocus = () => loadCounts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadCounts]);

  // Iter 43 — Coefficient config for the display-only Weighted column.
  // Reference-only: computed values are NEVER written back to Mongo, NEVER
  // used in creditCost/loyaltyRebateDue/moneyBackCredits math, NEVER conflated
  // with the admin-confirmed weighted_monthly_base (Baustein-3).
  useEffect(() => {
    api.get("/config/wager-coefficients").then((r) => setWagerCoeffCfg(r.data)).catch(() => {});
  }, []);
  // Iter 194 — mode-aware helpers. Delegate to the shared `weightedFor` so
  // total_only-mode players (categories all zero, single stored total) render
  // the correct weighted volume everywhere admin looks at self-reported data.
  const weightedCasino = useCallback((p) => {
    const cats = wagerCoeffCfg?.categories || [];
    if (cats.length === 0) return null;
    return Math.round(weightedFor("casino", p, wagerCoeffCfg));
  }, [wagerCoeffCfg]);
  const weightedCase = useCallback((p) => Math.round(weightedFor("case_opening", p, wagerCoeffCfg)), [wagerCoeffCfg]);
  const togglePlayerSort = (key) => {
    if (playerSortBy === key) setPlayerSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setPlayerSortBy(key); setPlayerSortDir(key === "name" ? "asc" : "desc"); }
  };
  const playerSortArrow = (key) => playerSortBy !== key ? "" : (playerSortDir === "asc" ? " ↑" : " ↓");

  // Iter 59 — Per-vertical state derivation shared by the filter bar and the
  // table row. Casino falls back to the legacy verificationTier only when
  // verticalVerification.casino is UNSET (matches iter 41-45 hardening).
  const playerVerticalState = (p) => {
    const vv = p.verticalVerification || {};
    const casinoSet = Object.prototype.hasOwnProperty.call(vv, "casino");
    const caseSet   = Object.prototype.hasOwnProperty.call(vv, "case_opening");
    const legacyCasinoVerified = !casinoSet && ["verified", "vip"].includes(p.verificationTier) && p.status === "approved";
    const casino = casinoSet ? (vv.casino ? "verified" : "declined") : (legacyCasinoVerified ? "verified" : "pending");
    const cases_ = caseSet ? (vv.case_opening ? "verified" : "declined") : "pending";
    return { casino, case_opening: cases_ };
  };
  const playerNeedsConfirmedValue = (p) => {
    const s = playerVerticalState(p);
    const anyVerified = s.casino === "verified" || s.case_opening === "verified";
    return anyVerified && !(Number(p.referenceVolume || 0) > 0);
  };

  const filteredSortedPlayers = useMemo(() => {
    // 1) filter (composable AND semantics, keeps sort intact per iter 57 pattern)
    const source = playerSearchResults !== null ? playerSearchResults : players;
    const filtered = source.filter((p) => {
      const vs = playerVerticalState(p);
      // Iter 79 — outer split (pending vs active). A player appears in
      // the pending tab if ANY vertical is still pending; and in the active
      // tab if ANY vertical is verified. A mixed player (casino verified /
      // case pending) legitimately appears in BOTH tabs.
      const playerScope = activeTab === "players-pending" ? "pending" : activeTab === "players-active" ? "active" : null;
      if (playerScope === "pending" && !(vs.casino === "pending" || vs.case_opening === "pending")) return false;
      if (playerScope === "active"  && !(vs.casino === "verified" || vs.case_opening === "verified")) return false;
      if (playerStatusFilter !== "all" && (p.status || "pending") !== playerStatusFilter) return false;
      if (playerCasinoFilter !== "all" && vs.casino !== playerCasinoFilter) return false;
      if (playerCaseFilter   !== "all" && vs.case_opening !== playerCaseFilter) return false;
      if (playerReadyOnly && !playerNeedsConfirmedValue(p)) return false;
      return true;
    });
    // 2) same sort logic as before
    const cmp = (a, b) => {
      if (playerSortBy === "name") return (a.alias || "").localeCompare(b.alias || "");
      return (a.createdAt || "").localeCompare(b.createdAt || "");
    };
    return [...filtered].sort((a, b) => playerSortDir === "asc" ? cmp(a, b) : cmp(b, a));
  }, [players, playerSearchResults, playerStatusFilter, playerCasinoFilter, playerCaseFilter, playerReadyOnly, playerSortBy, playerSortDir, activeTab]);

  // Live counts for the filter tabs — recomputed from the CURRENT players
  // array, independent of any other filter state so admins always see the
  // full bucket sizes on each tab (mirrors the streamers pattern).
  const playerCounts = useMemo(() => {
    const buckets = {
      all: players.length,
      status: { all: players.length, pending: 0, approved: 0 },
      casino: { all: players.length, verified: 0, declined: 0, pending: 0 },
      case_opening: { all: players.length, verified: 0, declined: 0, pending: 0 },
      ready: 0,
    };
    for (const p of players) {
      const s = p.status || "pending";
      if (s === "pending")  buckets.status.pending  += 1;
      if (s === "approved") buckets.status.approved += 1;
      const vs = playerVerticalState(p);
      buckets.casino[vs.casino]             = (buckets.casino[vs.casino] ?? 0) + 1;
      buckets.case_opening[vs.case_opening] = (buckets.case_opening[vs.case_opening] ?? 0) + 1;
      if (playerNeedsConfirmedValue(p)) buckets.ready += 1;
    }
    return buckets;
  }, [players]);

  const sortedPlayers = filteredSortedPlayers; // kept as alias — old references still work
  const fmtInt = (n) => (Number(n) || 0).toLocaleString();

  const setPlayerStatus = async (pid, status) => {
    try { await api.patch(`/players/${pid}/admin`, { status }); toast.success(`Player ${status}`); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const setPlayerTier = async (pid, verificationTier) => {
    try { await api.patch(`/players/${pid}/admin`, { verificationTier }); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const deletePlayer = async (pid) => {
    if (!window.confirm("Hard delete player and screenshot reference?")) return;
    try { await api.delete(`/players/${pid}`); toast.success("Player deleted"); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const setStreamerStatus = async (sid, status) => {
    try { await api.patch(`/streamers/${sid}/admin`, { status }); toast.success(`Streamer ${status}`); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // Iter 39 — Verify uses the review-writer endpoint (audit + streamerVerified=true).
  // This deliberately does NOT touch enabledVerticals; enablement is a manual,
  // separate action via PATCH /streamers/{sid}/enablement.
  const openReview = async (sid) => {
    try {
      const { data } = await api.get(`/streamers/${sid}`);
      setReviewOpen({ streamer: data });
      setReviewNote(data.adminNote || "");
      setProofUrls({});
      // Load contracts scoped to this streamer for the "Contracts" panel.
      // refs are already fetched globally by load(); filter client-side.
      setReviewRefs(refs.filter((r) => r.streamerId === sid));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const closeReview = () => { setReviewOpen(null); setReviewNote(""); setProofUrls({}); setReviewBusy(false); setCreditDelta(""); setCreditReason(""); };

  // Iter 61 — manual credit grant / remove. `sign` is +1 (grant) or -1 (remove).
  // Backend enforces (a) reason min 3 chars, (b) delta != 0, (c) two-step
  // confirm before availableCredits goes negative. This function just wires
  // the UX: post → on 409 would_go_negative, window.confirm → retry with
  // confirmNegative:true. On success the review modal state is refreshed so
  // the ledger display updates without a full reload.
  const doCreditAdjust = async (sign) => {
    if (!reviewOpen) return;
    const amt = Number(creditDelta);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a positive amount."); return; }
    if ((creditReason || "").trim().length < 3) { toast.error("A reason / reference is required (min 3 characters)."); return; }
    const delta = sign * amt;
    const submit = async (confirmNegative) => {
      const { data } = await api.post(`/streamers/${reviewOpen.streamer.id}/credits/adjust`, {
        delta, reason: creditReason.trim(), confirmNegative,
        grantType: creditGrantType || "adjustment",
      });
      return data;
    };
    setCreditBusy(true);
    try {
      let data;
      try {
        data = await submit(false);
      } catch (e) {
        const det = e?.response?.data?.detail;
        if (e?.response?.status === 409 && det?.code === "would_go_negative") {
          const ok = window.confirm(`${det.message}\n\nBefore: ${det.before}\nAfter:  ${det.after}\n\nProceed anyway?`);
          if (!ok) return;
          data = await submit(true);
        } else { throw e; }
      }
      toast.success(`${sign > 0 ? "Granted" : "Removed"} ${amt} · balance ${data.before} → ${data.after}`);
      // Refresh the modal's streamer doc so the ledger panel updates in place.
      const fresh = await api.get(`/streamers/${reviewOpen.streamer.id}`);
      setReviewOpen((old) => old ? { ...old, streamer: fresh.data } : old);
      setCreditDelta(""); setCreditReason(""); setCreditGrantType("adjustment");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Credit adjust failed")); }
    finally { setCreditBusy(false); }
  };
  const fetchProofUrl = async (key) => {
    if (proofUrls[key]) return;
    try {
      const { data } = await api.get(`/files/signed-url`, { params: { path: key } });
      setProofUrls((u) => ({ ...u, [key]: data.url || data.proxyUrl }));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const decide = async (decision) => {
    if (!reviewOpen) return;
    const needsNote = decision === "needs_more_info" || decision === "rejected";
    if (needsNote && !reviewNote.trim()) {
      toast.error("Add an admin note before this action.");
      return;
    }
    if (decision === "approved" && !window.confirm("Approve this streamer? This flips streamerVerified=true. Vertical enablement is NOT touched.")) return;
    if (decision === "rejected" && !window.confirm("Reject this streamer? They will not be able to enter the streamer-only area.")) return;
    setReviewBusy(true);
    try {
      await api.patch(`/streamers/${reviewOpen.streamer.id}/review`, { decision, adminNote: reviewNote });
      toast.success(`Review: ${decision}`);
      closeReview(); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setReviewBusy(false); }
  };

  const setPlayerVerticalState = async (pid, vertical, state) => {
    // Iter 45: tri-state writer (verified | declined | pending). Optional
    // reason prompt so the audit entry carries the "why". Cancel leaves the
    // current stored state untouched.
    const label = vertical === "casino" ? "Casino" : "Case-Pack";
    const verbMap = { verified: "verifying", declined: "declining", pending: "resetting" };
    const reason = window.prompt(`Optional reason for ${verbMap[state]} ${label} (leave blank to skip):`, "");
    if (reason === null) return; // admin cancelled
    const key = `${pid}:${vertical}`;
    setPvvBusy((old) => ({ ...old, [key]: true }));
    try {
      await api.patch(`/players/${pid}/vertical-verification`, { vertical, state, reason: reason || undefined });
      toast.success(`${label}: ${state}`);
      // Refresh both the global list AND the currently-open modal doc so the
      // three-state UI reflects the new value immediately (no close/reopen).
      const fresh = await api.get(`/players/${pid}`);
      setPlayerDetail(fresh.data);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setPvvBusy((old) => { const n = { ...old }; delete n[key]; return n; });
    }
  };

  const setPlayerVerticalVerification = async (pid, vertical, verified) => {
    // Manual-only per-vertical player verification (iteration 19). Optional
    // reason prompt so the audit entry carries the "why".
    const label = vertical === "casino" ? "Casino" : "Case-Pack";
    const reason = window.prompt(`Optional reason for ${verified ? "verifying" : "revoking"} ${label} for this player (leave blank to skip):`, "");
    if (reason === null) return;
    const key = `${pid}:${vertical}`;
    setPvvBusy((old) => ({ ...old, [key]: true }));
    try {
      await api.patch(`/players/${pid}/vertical-verification`, { vertical, verified, reason: reason || undefined });
      toast.success(`${label} ${verified ? "verified" : "revoked"}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setPvvBusy((old) => { const n = { ...old }; delete n[key]; return n; });
    }
  };

  const openPvvHistory = async (player) => {
    try {
      const { data } = await api.get(`/players/${player.id}/vertical-verification-log`);
      setPvvHistory({ player, rows: data || [] });
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const closePvvHistory = () => setPvvHistory(null);

  // Iter 42 — Admin Player Detail modal handlers. Full player doc (with PII)
  // is fetched fresh from GET /players/{id}. That endpoint is admin-gated
  // server-side (iter 41 fix) so streamers get 403.
  const openPlayerDetail = async (pid) => {
    setPlayerDetailBusy(true);
    setPlayerProofUrls({});
    try {
      const { data } = await api.get(`/players/${pid}`);
      setPlayerDetail(data);
      setRefVolDraft((old) => ({ ...old, [pid]: (Number(data.referenceVolume || 0) || "") }));
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setPlayerDetailBusy(false); }
  };
  const closePlayerDetail = () => { setPlayerDetail(null); setPlayerProofUrls({}); };
  const fetchPlayerProofUrl = async (key) => {
    if (playerProofUrls[key]) return;
    try {
      const { data } = await api.get(`/files/signed-url`, { params: { path: key } });
      setPlayerProofUrls((u) => ({ ...u, [key]: data.url || data.proxyUrl }));
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setStreamerEnablement = async (sid, vertical, enabled) => {
    // Manual-only per-vertical toggle. Optional reason prompt so the audit
    // entry can carry the "why". Cancel leaves the current state untouched.
    const label = vertical === "casino" ? "Casino" : "Case-Pack";
    const reason = window.prompt(`Optional reason for ${enabled ? "enabling" : "disabling"} ${label} for this streamer (leave blank to skip):`, "");
    if (reason === null) return; // admin cancelled
    const key = `${sid}:${vertical}`;
    setEnablementBusy((old) => ({ ...old, [key]: true }));
    try {
      await api.patch(`/streamers/${sid}/enablement`, { vertical, enabled, reason: reason || undefined });
      toast.success(`${label} ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setEnablementBusy((old) => { const n = { ...old }; delete n[key]; return n; });
    }
  };

  const openEnablementHistory = async (streamer) => {
    try {
      const { data } = await api.get(`/streamers/${streamer.id}/enablement-log`);
      setEnablementHistory({ streamer, rows: data || [] });
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const closeEnablementHistory = () => setEnablementHistory(null);

  // Iter 57 -- streamer delete flow. Frontend confirms via typed-email match;
  // the real safety is the backend 409 guard (active contracts, pending topups).
  const openDeleteStreamer = (streamer) => { setDeleteOpen({ streamer }); setDeleteConfirmText(""); };
  const closeDeleteStreamer = () => { setDeleteOpen(null); setDeleteConfirmText(""); };
  const doDeleteStreamer = async () => {
    if (!deleteOpen) return;
    const s = deleteOpen.streamer;
    if ((deleteConfirmText || "").trim().toLowerCase() !== String(s.email || "").trim().toLowerCase()) {
      toast.error("Typed email does not match. Delete cancelled.");
      return;
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/streamers/${s.id}`);
      toast.success(`Streamer deleted. Proofs purged from R2: ${data.proofsPurged || 0}${data.r2FailedCount ? ` (R2 errors: ${data.r2FailedCount})` : ""}.`);
      closeDeleteStreamer();
      load();
    } catch (e) {
      // 409 detail may be an object with {code, message, count}. apiErrorMessage
      // already stringifies, but keep the specific message visible.
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally {
      setDeleteBusy(false);
    }
  };

  // Iter 62 — debounced admin search. Fires at >=2 chars, otherwise clears the
  // server-search state so the local list is used. Composes with the existing
  // verification/enablement filter + sort via the source-list swap in
  // filteredStreamers / filteredSortedPlayers below.
  useEffect(() => {
    const raw = (streamerSearch || "").trim();
    if (raw.length < 2) { setStreamerSearchResults(null); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/streamers/search", { params: { q: raw } });
        setStreamerSearchResults(Array.isArray(data) ? data : []);
      } catch (e) { toast.error(apiErrorMessage(e, "Search failed")); }
    }, 300);
    return () => clearTimeout(h);
  }, [streamerSearch]);
  useEffect(() => {
    const raw = (playerSearch || "").trim();
    if (raw.length < 2) { setPlayerSearchResults(null); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/players/search", { params: { q: raw } });
        setPlayerSearchResults(Array.isArray(data) ? data : []);
      } catch (e) { toast.error(apiErrorMessage(e, "Search failed")); }
    }, 300);
    return () => clearTimeout(h);
  }, [playerSearch]);

  const filteredStreamers = useMemo(() => {
    const has = (s, v) => Array.isArray(s.enabledVerticals) && s.enabledVerticals.includes(v);
    const source = streamerSearchResults !== null ? streamerSearchResults : streamers;
    const rows = source.filter((s) => {
      // Iter 79 — outer split (pending vs active). Active = verificationStatus === "approved";
      // Pending = anything else (not_submitted / pending / needs_more_info / rejected).
      const streamerScope = activeTab === "streamers-pending" ? "pending" : activeTab === "streamers-active" ? "active" : null;
      const vstat = s.verificationStatus || "not_submitted";
      if (streamerScope === "pending" && vstat === "approved") return false;
      if (streamerScope === "active"  && vstat !== "approved") return false;
      if (verificationFilter !== "all" && vstat !== verificationFilter) return false;
      if (enablementFilter === "casino") return has(s, "casino");
      if (enablementFilter === "case_opening") return has(s, "case_opening");
      if (enablementFilter === "neither") return !has(s, "casino") && !has(s, "case_opening");
      return true;
    });
    const cmp = (a, b) => {
      if (sortBy === "name") return (a.displayName || "").localeCompare(b.displayName || "");
      if (sortBy === "credits") {
        // Iter 60 — read from creditLedger.availableCredits with a legacy fallback.
        const av = (x) => Number(x?.creditLedger?.availableCredits ?? x?.creditBalance ?? 0);
        return av(a) - av(b);
      }
      // registered — createdAt ISO strings sort lexicographically like timestamps
      return (a.createdAt || "").localeCompare(b.createdAt || "");
    };
    rows.sort((a, b) => sortDir === "asc" ? cmp(a, b) : cmp(b, a));
    return rows;
  }, [streamers, streamerSearchResults, verificationFilter, enablementFilter, sortBy, sortDir, activeTab]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };
  const sortArrow = (key) => sortBy !== key ? "" : (sortDir === "asc" ? " ↑" : " ↓");

  // Iter 40: enable/disable a vertical from inside the review modal via the
  // existing per-vertical enablement writer. Independent from verification;
  // never auto-fires from approve.
  const toggleReviewEnablement = async (vertical, enabled) => {
    if (!reviewOpen) return;
    setReviewBusy(true);
    try {
      const { data } = await api.patch(`/streamers/${reviewOpen.streamer.id}/enablement`, { vertical, enabled });
      setReviewOpen((r) => ({ ...r, streamer: { ...r.streamer, enabledVerticals: data.enabledVerticals || [] } }));
      toast.success(`${vertical} ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setReviewBusy(false); }
  };

  const addCasino = async () => {
    try { await api.post("/casinos", newCasino); setNewCasino({ name: "", notes: "", active: true }); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const deleteCasino = async (id) => {
    try { await api.delete(`/casinos/${id}`); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  // 2026-02 — Tiers tab is now wired to the real `commissionTierConfig`.
  // Every mutation (add row, delete row, bulk-import) replaces the full
  // per-vertical threshold list via a single PATCH so the versioning +
  // history archive on the backend stays authoritative.
  const currentTierCfg = tiersCfg?.[tiersVertical] || null;
  const currentThresholds = (currentTierCfg?.thresholds || []).slice().sort((a, b) => a.minVolume - b.minVolume);
  const tiersMonotonic = (() => {
    // Rates must be non-increasing as volume rises (degressive curve).
    let last = Infinity;
    for (const t of currentThresholds) {
      if (Number(t.rate) > last) return false;
      last = Number(t.rate);
    }
    return true;
  })();
  const patchTierCfg = async (thresholds) => {
    if (!Array.isArray(thresholds) || thresholds.length === 0) {
      toast.error("At least one tier required");
      return;
    }
    setTiersBusy(true);
    try {
      await api.patch(`/config/commission-tiers/${tiersVertical}`, {
        thresholds: thresholds.map((t) => ({ minVolume: Number(t.minVolume), rate: Number(t.rate) })),
      });
      toast.success(`${tiersVertical} tiers updated`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setTiersBusy(false);
    }
  };
  const addTierRow = () => {
    const mv = Number(tiersDraft.minVolume);
    const rr = Number(tiersDraft.rate);
    if (!Number.isFinite(mv) || mv < 0) { toast.error("Invalid volume"); return; }
    if (!Number.isFinite(rr) || rr < 0 || rr > 1) { toast.error("Rate must be between 0 and 1"); return; }
    if (currentThresholds.some((t) => Number(t.minVolume) === mv)) { toast.error(`Tier for minVolume ${mv} already exists`); return; }
    const next = [...currentThresholds, { minVolume: mv, rate: rr }].sort((a, b) => a.minVolume - b.minVolume);
    setTiersDraft({ minVolume: "", rate: "" });
    patchTierCfg(next);
  };
  const removeTierRow = (idx) => {
    if (currentThresholds.length <= 1) { toast.error("At least one tier required"); return; }
    const next = currentThresholds.filter((_, i) => i !== idx);
    patchTierCfg(next);
  };
  const parseBulk = () => {
    // Accept "minVolume,rate" one per line. Also accept whitespace/tabs.
    const rows = [];
    const errors = [];
    tiersBulkText.split(/\r?\n/).forEach((raw, i) => {
      const line = raw.trim();
      if (!line || line.startsWith("#")) return;
      const parts = line.split(/[\s,;\t]+/).filter(Boolean);
      if (parts.length < 2) { errors.push(`line ${i + 1}: expected "volume, rate"`); return; }
      const mv = Number(parts[0]);
      const rr = Number(parts[1]);
      if (!Number.isFinite(mv) || mv < 0) errors.push(`line ${i + 1}: bad volume`);
      else if (!Number.isFinite(rr) || rr < 0 || rr > 1) errors.push(`line ${i + 1}: rate must be 0..1`);
      else rows.push({ minVolume: mv, rate: rr });
    });
    if (errors.length) { setTiersBulkPreview({ error: errors.slice(0, 5).join("; ") }); return; }
    if (!rows.length) { setTiersBulkPreview({ error: "No rows parsed" }); return; }
    rows.sort((a, b) => a.minVolume - b.minVolume);
    // Detect broken monotonicity for the preview warning
    let last = Infinity, mono = true;
    for (const r of rows) { if (r.rate > last) { mono = false; break; } last = r.rate; }
    setTiersBulkPreview({ rows, monotonic: mono });
  };
  const applyBulk = () => {
    if (!tiersBulkPreview?.rows?.length) return;
    patchTierCfg(tiersBulkPreview.rows);
    setTiersBulkText("");
    setTiersBulkPreview(null);
  };

  const addRef = async () => {
    try {
      await api.post("/referrals", {
        ...newRef,
        referenceVolume: Number(newRef.referenceVolume),
        underCodeWagerStart: Number(newRef.underCodeWagerStart),
        committedVolume: Number(newRef.committedVolume),
      });
      toast.success("Referral created");
      setNewRef({ ...newRef, playerId: "", streamerCode: "", underCodeWagerStart: 0, committedVolume: 0, referenceVolume: 0, wasExistingCustomer: false });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const updateRefField = async (rid, patch) => {
    try {
      const val = Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, v === "" ? null : (typeof v === "boolean" ? v : Number(v))]));
      await api.patch(`/referrals/${rid}`, val); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const saveLoyalty = async (val) => { try { await api.patch("/config", { loyaltyRebatePct: Number(val) }); toast.success("Config saved"); load(); } catch (e) { toast.error(apiErrorMessage(e)); } };

  const saveDepositAddress = async (net, addr) => {
    const current = cfg.depositAddresses || cfg.depositWallets || {};
    const merged = { ...current, [net]: addr };
    try { await api.patch("/config", { depositAddresses: merged }); toast.success("Address saved"); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleNetwork = async (value, enabled) => {
    const nets = (cfg.networks || []).map((n) => n.value === value ? { ...n, enabled } : n);
    try { await api.patch("/config", { networks: nets }); toast.success(enabled ? `${value} enabled` : `${value} disabled`); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setTopupStatus = async (tid, status) => {
    try { await api.patch(`/credit-topups/${tid}`, { status }); toast.success(`Top-up ${status}`); load(); } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  // Iter 93 → Iter 94 — Confirm/reject flow now lives in the SHARED
  // TopupDetailModal component. `topupDetailOpen` holds the topup row that
  // should be rendered by the modal (opened from either the TOPUPS tab or
  // the Active Streamer modal's "Credit purchases" block).
  const [topupDetailOpen, setTopupDetailOpen] = useState(null);

  const previewImport = async () => {
    if (!importFile) { toast.error("Choose a CSV"); return; }
    const fd = new FormData(); fd.append("file", importFile);
    setImportBusy(true);
    try { const { data } = await api.post("/referrals/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImportPreview(data); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setImportBusy(false); }
  };
  const applyImport = async () => {
    if (!importPreview) return;
    const rows = importPreview.rows.filter((r) => !r.skipped).map((r) => ({ referralId: r.referralId, ...r.after }));
    setImportBusy(true);
    try { const { data } = await api.post("/referrals/import-apply", { rows }); toast.success(`Applied ${data.applied} row(s)`); setImportPreview(null); setImportFile(null); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setImportBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-2">Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">{t("dash.admin")}</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10 mb-10" data-testid="admin-stats">
        {(() => {
          // Iter 58 -- live stats. Every tile below reads from /admin/stats which
          // recomputes on each dashboard load; a new registration bumps the
          // matching count on next load/refresh. The $ value uses the platform's
          // admin-confirmed billing figure (creditCost, derived from measured
          // wager -- never self-reported referenceVolume).
          const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const tiles = [
            ["Pending Players",       stats.pendingPlayers,       "pending-players"],
            ["Pending Streamers",     stats.pendingStreamers,     "pending-streamers"],
            ["Approved Players",      stats.approvedPlayers,      "approved-players"],
            ["Verified Streamers",    stats.verifiedStreamers,    "verified-streamers"],
            ["Active Contracts",             stats.activeContracts,              "active-contracts"],
            ["Commission (Active) $",        fmtMoney(stats.activeContractsCommission), "active-commission"],
            ["Due Payouts",           stats.duePayouts,           "due-payouts"],
          ];
          return tiles.map(([k, v, tid]) => (
            <div key={k} className="bg-[#0B0E14] p-4" data-testid={`admin-stat-${tid}`}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">{k}</div>
              <div className="font-display text-3xl font-black mt-1" data-testid={`admin-stat-${tid}-value`}>{v ?? 0}</div>
            </div>
          ));
        })()}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none bg-transparent border border-white/15 p-0 h-auto flex flex-wrap">
          {[
            // [value, label, countKey (null=no number), isActionRelevant (gold-when->0)]
            ["players-pending",   "Pending Players",   "pendingPlayers",   true],
            ["players-active",    "Active Players",    "activePlayers",    false],
            ["streamers-pending", "Pending Streamers", "pendingStreamers", true],
            ["streamers-active",  "Active Streamers",  "activeStreamers",  false],
            ["referrals",         "Referrals",         "referrals",        false],
            ["contract-review",   "Contract Review",   "contractReview",   true],
            ["change-requests",   "Change Requests",   "changeRequests",   true],
            ["cancellations",     "Cancellations",     "cancellations",    true],
            ["contracts",         "Contracts",         "contracts",        false],
            ["topups-pending",    "Topups Pending",    "topupsPending",    true],
            ["topups-done",       "Topups Done",       "topupsDone",       false],
            ["payouts-pending",   "Payouts Pending",   "payoutsPending",   true],
            ["payouts-done",      "Payouts Done",      "payoutsDone",      false],
            ["support",           "Support",           "support",          true],
            ["casinos",           "Casinos",           "casinos",          false],
            ["tiers",             "Tiers",             "tiers",            false],
            ["import",            "Import",            null,               false],
            ["audit",             "Audit",             "audit",            false],
            ["config",            "Config",            null,               false],
          ].map(([v, label, countKey, actionRel]) => {
            const raw = countKey && tabCounts ? tabCounts[countKey] : null;
            const hasNum = typeof raw === "number";
            const gold = actionRel && hasNum && raw > 0;
            return (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black px-3 h-10 font-mono text-[10px] uppercase tracking-widest whitespace-nowrap"
              >
                {label}
                {countKey && hasNum && (
                  <span
                    data-testid={`tab-count-${v}`}
                    className={`ml-1.5 tabular-nums ${gold ? "text-[#F5B841] font-bold" : "text-[#8B97AE]"} data-[state=active]:text-inherit`}
                  >({raw})</span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {["players-pending", "players-active"].map((_scopeV) => (
        <TabsContent key={_scopeV} value={_scopeV} className="mt-6">
          <div className="mb-3 flex items-center gap-2" data-testid="player-search-bar">
            <Input
              data-testid="player-search-input"
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              placeholder="Search all players — name, email, id, wallet, gaming account, referral code…"
              className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs"
            />
            {playerSearchResults !== null && (
              <button
                data-testid="player-search-clear"
                onClick={() => setPlayerSearch("")}
                className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-white"
              >clear</button>
            )}
            <span data-testid="player-search-status" className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] whitespace-nowrap">
              {playerSearchResults === null ? "local" : `server · ${playerSearchResults.length} match`}
            </span>
          </div>
          <div className="mb-3 space-y-2" data-testid="player-filter-bar">
            {(() => {
              const btn = (v, l, count, active, onClick, tid) => (
                <button
                  key={v}
                  data-testid={tid}
                  onClick={onClick}
                  className={`px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors ${active ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"}`}
                >{l} <span data-testid={`${tid}-count`} className="ml-1 opacity-70">({count})</span></button>
              );
              return (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] w-40">Approval status</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
                        ["all",      "All",      playerCounts.status.all],
                        ["pending",  "Pending",  playerCounts.status.pending],
                        ["approved", "Approved", playerCounts.status.approved],
                      ].map(([v, l, c]) => btn(v, l, c, playerStatusFilter === v, () => setPlayerStatusFilter(v), `player-filter-status-${v}`))}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] w-40">Casino verification</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
                        ["all",      "All",      playerCounts.casino.all],
                        ["verified", "Verified", playerCounts.casino.verified],
                        ["declined", "Declined", playerCounts.casino.declined],
                        ["pending",  "Pending",  playerCounts.casino.pending],
                      ].map(([v, l, c]) => btn(v, l, c, playerCasinoFilter === v, () => setPlayerCasinoFilter(v), `player-filter-casino-${v}`))}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] w-40">Case-Pack verification</div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {[
                        ["all",      "All",      playerCounts.case_opening.all],
                        ["verified", "Verified", playerCounts.case_opening.verified],
                        ["declined", "Declined", playerCounts.case_opening.declined],
                        ["pending",  "Pending",  playerCounts.case_opening.pending],
                      ].map(([v, l, c]) => btn(v, l, c, playerCaseFilter === v, () => setPlayerCaseFilter(v), `player-filter-case-${v}`))}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] w-40">Quick filter</div>
                    <button
                      data-testid="player-filter-ready-only"
                      onClick={() => setPlayerReadyOnly((v) => !v)}
                      className={`px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors ${playerReadyOnly ? "bg-[#F5B841] text-[#04150F] border-[#F5B841]" : "border-[#F5B841]/50 text-[#F5B841] hover:bg-[#F5B841]/10"}`}
                    >Needs confirmed value <span className="ml-1 opacity-70">({playerCounts.ready})</span></button>
                    <div className="text-[10px] font-mono text-[#8B97AE]" data-testid="player-filter-shown">Showing {filteredSortedPlayers.length} / {players.length}</div>
                  </div>
                </>
              );
            })()}
          </div>
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-approve-toolbar">
            <div className="text-xs font-mono uppercase tracking-widest text-[#8B97AE]">
              {selectedCount > 0 ? `${selectedCount} selected` : `${eligibleIds.length} eligible for bulk approve`}
              <span className="ml-3 text-[#F5B841]">Guardrail: each player still needs email confirmation + admin-set reference volume &gt; 0.</span>
            </div>
            <Button
              data-testid="admin-bulk-approve-btn"
              onClick={bulkApprove}
              disabled={bulkBusy || !anyEligibleSelected}
              className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-9 px-4 font-semibold text-xs uppercase tracking-widest transition-colors disabled:opacity-40"
            >
              {bulkBusy ? "Approving…" : `Bulk approve${selectedCount ? ` (${selectedCount})` : ""}`}
            </Button>
          </div>
          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-players-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10">
                <th className="text-left p-3 w-8">
                  <Checkbox
                    data-testid="admin-bulk-select-all"
                    checked={eligibleIds.length > 0 && eligibleIds.every((id) => bulkSel[id])}
                    onCheckedChange={toggleAll}
                    className="rounded-none border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F]"
                  />
                </th>
                <th className="text-left p-3 cursor-pointer hover:text-[#2DD4A7]" data-testid="player-sort-name" onClick={() => togglePlayerSort("name")}>Name{playerSortArrow("name")}</th>
                <th className="text-left p-3 cursor-pointer hover:text-[#2DD4A7]" data-testid="player-sort-registered" onClick={() => togglePlayerSort("registered")}>Registered{playerSortArrow("registered")}</th>
                <th className="text-left p-3" data-testid="player-col-weighted">Weighted · self-reported (est.)</th>
                <th className="text-left p-3">Contract</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Actions</th>
              </tr></thead>
              <tbody>
                {sortedPlayers.map((p) => {
                  const eligible = p.status === "pending" && p.emailVerified && Number(p.referenceVolume || 0) > 0;
                  const skipReason = skipReasons[p.id];
                  const skipCopy = {
                    no_reference_volume: "Skipped: reference volume not set",
                    email_not_confirmed: "Skipped: email not confirmed",
                    already_approved: "Skipped: already approved",
                    not_found: "Skipped: not found",
                  };
                  const hasActiveContract = refs.some((r) => r.playerId === p.id && ["active", "closing"].includes(r.status));
                  const wCasino = weightedCasino(p);
                  const wCase   = weightedCase(p);
                  const hasCasinoData = wCasino !== null && hasCasinoSelfReported(p);
                  const hasCaseData   = wCase > 0;
                  const registered = p.createdAt ? new Date(p.createdAt).toISOString().slice(0, 10) : "—";
                  return (
                  <tr key={p.id} className="border-b border-white/5 align-top">
                    <td className="p-3 pt-4 w-8">
                      <Checkbox
                        data-testid={`admin-bulk-select-${p.id}`}
                        checked={!!bulkSel[p.id]}
                        onCheckedChange={(v) => setBulkSel((old) => ({ ...old, [p.id]: !!v }))}
                        disabled={!eligible}
                        className="rounded-none border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F] disabled:opacity-30"
                      />
                    </td>
                    <td className="p-3">
                      <button
                        data-testid={`player-open-detail-${p.id}`}
                        onClick={() => openPlayerDetail(p.id)}
                        className="text-[#2DD4A7] hover:text-white underline underline-offset-2 text-left"
                      >{p.alias}</button>
                      {skipReason && (
                        <div data-testid={`admin-player-skip-${p.id}`} className="mt-1 inline-flex items-center gap-1.5 border border-[#F5B841]/50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">
                          {skipCopy[skipReason] || `Skipped: ${skipReason}`}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs tabular-nums" data-testid={`admin-player-registered-${p.id}`}>{registered}</td>
                    <td className="p-3 font-mono text-xs tabular-nums" data-testid={`admin-player-weighted-${p.id}`}>
                      <div className="leading-tight">
                        <div><span className="text-[#8B97AE]">Casino:</span> {hasCasinoData ? `$${fmtInt(wCasino)}` : <span className="text-[#8B97AE]">—</span>}</div>
                        <div><span className="text-[#8B97AE]">Case:</span>   {hasCaseData   ? `$${fmtInt(wCase)}`   : <span className="text-[#8B97AE]">—</span>}</div>
                      </div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] mt-1">est. · never billed</div>
                    </td>
                    <td className="p-3" data-testid={`admin-player-contract-${p.id}`}>
                      {hasActiveContract ? (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                          <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Active
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">None</span>
                      )}
                    </td>
                    <td className="p-3" data-testid={`admin-player-verticals-${p.id}`}>
                      {(() => {
                        const _vv = p.verticalVerification || {};
                        const casinoSet = Object.prototype.hasOwnProperty.call(_vv, "casino");
                        const caseSet   = Object.prototype.hasOwnProperty.call(_vv, "case_opening");
                        const legacyCasinoVerified = !casinoSet && ["verified", "vip"].includes(p.verificationTier) && p.status === "approved";
                        const cState = casinoSet ? (_vv.casino ? "verified" : "declined") : (legacyCasinoVerified ? "verified" : "pending");
                        const kState = caseSet ? (_vv.case_opening ? "verified" : "declined") : "pending";
                        const anyVerified = cState === "verified" || kState === "verified";
                        const savedRef = Number(p.referenceVolume || 0);
                        const needsConfirmed = anyVerified && !(savedRef > 0);
                        const chip = (label, state, tid) => {
                          const tone = state === "verified" ? "border-emerald-500/50 text-emerald-400" : state === "declined" ? "border-[#F26D6D]/60 text-[#F26D6D]" : "border-[#F5B841]/50 text-[#F5B841]";
                          const glyph = state === "verified" ? "✓" : state === "declined" ? "✕" : "…";
                          return (
                            <span data-testid={tid} className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${tone}`}>
                              {label} {glyph}
                            </span>
                          );
                        };
                        return (
                          <div className="flex flex-col gap-1">
                            {chip("Casino", cState, `admin-player-casino-${p.id}`)}
                            {chip("Case", kState, `admin-player-case-${p.id}`)}
                            {needsConfirmed && (
                              <span data-testid={`admin-player-needs-confirmed-${p.id}`} className="inline-flex items-center gap-1 border border-[#F5B841]/50 text-[#F5B841] px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest">
                                needs confirmed value
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      <button
                        data-testid={`player-details-${p.id}`}
                        onClick={() => openPlayerDetail(p.id)}
                        className="text-[#2DD4A7] hover:text-white transition-colors text-xs font-mono uppercase tracking-widest"
                      >Details</button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>

          {pvvHistory && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="pvv-history-modal" onClick={closePvvHistory}>
              <div className="bg-[#0B0E14] border border-white/15 max-w-3xl w-full mt-16" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">Per-vertical verification history</div>
                    <div className="font-display text-2xl font-black tracking-tighter">{pvvHistory.player.alias}</div>
                  </div>
                  <button data-testid="pvv-history-close" onClick={closePvvHistory} className="text-[#8B97AE] hover:text-white text-xs font-mono uppercase tracking-widest">Close</button>
                </div>
                <div className="p-5">
                  {pvvHistory.rows.length === 0 ? (
                    <div className="text-xs font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="pvv-history-empty">No verification changes yet.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                        <tr className="border-b border-white/10">
                          <th className="text-left p-2">When (UTC)</th>
                          <th className="text-left p-2">Vertical</th>
                          <th className="text-left p-2">Change</th>
                          <th className="text-left p-2">Admin</th>
                          <th className="text-left p-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pvvHistory.rows.map((r) => {
                          const oldTxt = r.oldValue === null || r.oldValue === undefined ? "unset" : (r.oldValue ? "verified" : "revoked");
                          const newTxt = r.newValue ? "verified" : "revoked";
                          return (
                          <tr key={r.id} className="border-b border-white/5" data-testid={`pvv-history-row-${r.id}`}>
                            <td className="p-2 font-mono text-xs">{(r.createdAt || "").replace("T", " ").slice(0, 19)}</td>
                            <td className="p-2 font-mono text-xs">{r.vertical === "case_opening" ? "Case-Pack" : "Casino"}</td>
                            <td className="p-2 font-mono text-xs">{oldTxt} → <span className={r.newValue ? "text-emerald-400" : "text-[#F5B841]"}>{newTxt}</span></td>
                            <td className="p-2 font-mono text-xs">{r.adminEmail || r.adminId}</td>
                            <td className="p-2 text-xs text-[#E8ECF4]">{r.reason || <span className="text-[#8B97AE]">—</span>}</td>
                          </tr>
                        );})}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>
        ))}

        {["streamers-pending", "streamers-active"].map((_scopeV) => (
        <TabsContent key={_scopeV} value={_scopeV} className="mt-6">
          <div className="mb-3 flex items-center gap-2" data-testid="streamer-search-bar">
            <Input
              data-testid="streamer-search-input"
              value={streamerSearch}
              onChange={(e) => setStreamerSearch(e.target.value)}
              placeholder="Search all streamers — name, email, id, channel, txReference, sender address…"
              className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs"
            />
            {streamerSearchResults !== null && (
              <button
                data-testid="streamer-search-clear"
                onClick={() => setStreamerSearch("")}
                className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-white"
              >clear</button>
            )}
            <span data-testid="streamer-search-status" className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] whitespace-nowrap">
              {streamerSearchResults === null ? "local" : `server · ${streamerSearchResults.length} match`}
            </span>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-3" data-testid="streamer-verification-toolbar">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Verification review</div>
            <div className="flex items-center gap-1">
              {[
                ["all", "All"],
                ["pending", "Pending"],
                ["needs_more_info", "Needs info"],
                ["approved", "Verified"],
                ["rejected", "Rejected"],
              ].map(([v, l]) => {
                const count = v === "all"
                  ? streamers.length
                  : streamers.filter((s) => (s.verificationStatus || "not_submitted") === v).length;
                return (
                  <button
                    key={v}
                    data-testid={`streamer-review-filter-${v}`}
                    onClick={() => setVerificationFilter(v)}
                    className={`px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors ${verificationFilter === v ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"}`}
                  >
                    {l} <span data-testid={`streamer-review-filter-count-${v}`} className="ml-1 opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="streamer-enablement-toolbar">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Per-vertical enablement</div>
            <div className="flex items-center gap-1">
              {[
                ["all", "All"],
                ["casino", "Casino enabled"],
                ["case_opening", "Case-Pack enabled"],
                ["neither", "Neither"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`streamer-filter-${v}`}
                  onClick={() => setEnablementFilter(v)}
                  className={`px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors ${enablementFilter === v ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">
              Manual only · verification does NOT auto-enable a vertical
            </div>
          </div>
          <div className="border border-white/15 overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-streamers-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10">
                <th className="text-left p-3 cursor-pointer hover:text-[#2DD4A7]" data-testid="streamer-sort-name" onClick={() => toggleSort("name")}>Name{sortArrow("name")}</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3 cursor-pointer hover:text-[#2DD4A7]" data-testid="streamer-sort-registered" onClick={() => toggleSort("registered")}>Registered{sortArrow("registered")}</th>
                <th className="text-left p-3 cursor-pointer hover:text-[#2DD4A7]" data-testid="streamer-sort-credits" onClick={() => toggleSort("credits")}>Credits{sortArrow("credits")}</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Casino</th>
                <th className="text-left p-3">Case-Pack</th>
                <th className="text-left p-3">Actions</th>
              </tr></thead>
              <tbody>
                {filteredStreamers.map((s) => {
                  const casinoOn = Array.isArray(s.enabledVerticals) && s.enabledVerticals.includes("casino");
                  const caseOn = Array.isArray(s.enabledVerticals) && s.enabledVerticals.includes("case_opening");
                  const casinoBusy = enablementBusy[`${s.id}:casino`];
                  const caseBusy = enablementBusy[`${s.id}:case_opening`];
                  return (
                  <tr key={s.id} className="border-b border-white/5">
                    <td className="p-3">
                      <button
                        data-testid={`streamer-open-detail-${s.id}`}
                        onClick={() => openReview(s.id)}
                        className="text-[#2DD4A7] hover:text-white underline underline-offset-2 text-left"
                      >{s.displayName}</button>
                    </td>
                    <td className="p-3 font-mono text-xs">{s.email}</td>
                    <td className="p-3 font-mono text-xs" data-testid={`streamer-registered-${s.id}`}>{s.createdAt ? new Date(s.createdAt).toISOString().slice(0, 10) : "—"}</td>
                    <td className="p-3 font-mono" data-testid={`streamer-available-credits-${s.id}`}>{s.creditLedger?.availableCredits ?? s.creditBalance ?? 0}</td>
                    <td className="p-3"><Pill tone={s.status === "verified" ? "ok" : s.status === "suspended" ? "danger" : "warn"}>{s.status}</Pill></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Pill tone={casinoOn ? "ok" : "default"} data-testid={`streamer-casino-badge-${s.id}`}>{casinoOn ? "enabled" : "disabled"}</Pill>
                        <button
                          data-testid={`streamer-casino-toggle-${s.id}`}
                          disabled={casinoBusy}
                          onClick={() => setStreamerEnablement(s.id, "casino", !casinoOn)}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-[#F5B841] transition-colors disabled:opacity-40"
                        >
                          {casinoBusy ? "…" : (casinoOn ? "disable" : "enable")}
                        </button>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Pill tone={caseOn ? "ok" : "default"} data-testid={`streamer-case-badge-${s.id}`}>{caseOn ? "enabled" : "disabled"}</Pill>
                        <button
                          data-testid={`streamer-case-toggle-${s.id}`}
                          disabled={caseBusy}
                          onClick={() => setStreamerEnablement(s.id, "case_opening", !caseOn)}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-[#F5B841] transition-colors disabled:opacity-40"
                        >
                          {caseBusy ? "…" : (caseOn ? "disable" : "enable")}
                        </button>
                      </div>
                    </td>
                    <td className="p-3 space-x-3 whitespace-nowrap">
                      <Pill tone={s.verificationStatus === "approved" ? "ok" : s.verificationStatus === "rejected" ? "danger" : (s.verificationStatus === "pending" || s.verificationStatus === "needs_more_info") ? "warn" : "default"} data-testid={`streamer-vstatus-${s.id}`}>{s.verificationStatus || "not_submitted"}</Pill>
                      <button data-testid={`streamer-review-${s.id}`} onClick={() => openReview(s.id)} className="text-[#2DD4A7] hover:text-white transition-colors text-xs font-mono uppercase">Review</button>
                      <button data-testid={`streamer-suspend-${s.id}`} onClick={() => setStreamerStatus(s.id, "suspended")} className="text-[#F26D6D] hover:text-[#E8ECF4] transition-colors text-xs font-mono uppercase">{t("dash.suspend")}</button>
                      <button data-testid={`streamer-history-${s.id}`} onClick={() => openEnablementHistory(s)} className="text-[#8B97AE] hover:text-[#2DD4A7] transition-colors text-xs font-mono uppercase">History</button>
                      <button data-testid={`streamer-delete-${s.id}`} onClick={() => openDeleteStreamer(s)} className="text-[#F26D6D] hover:text-white transition-colors text-xs font-mono uppercase">Delete</button>
                    </td>
                  </tr>
                );})}
                {filteredStreamers.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-xs font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="streamers-empty">No streamers match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {reviewOpen && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="streamer-review-modal" onClick={closeReview}>
              <div onClick={(e) => e.stopPropagation()} className="bg-[#0B0E14] border border-white/15 max-w-3xl w-full p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-2xl font-black tracking-tighter" data-testid="review-displayName">{reviewOpen.streamer.displayName}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="review-email">{reviewOpen.streamer.email} · email-verified: {String(!!reviewOpen.streamer.emailVerified)}</div>
                  </div>
                  <button onClick={closeReview} className="text-[#8B97AE] hover:text-white text-sm font-mono">close</button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Platform</span><div data-testid="review-platform">{reviewOpen.streamer.onboarding?.platform || "—"}</div></div>
                  <div><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Channel URL</span><div className="truncate" data-testid="review-channelUrl"><a href={reviewOpen.streamer.onboarding?.channelUrl} target="_blank" rel="noreferrer noopener" className="text-[#2DD4A7] hover:underline">{reviewOpen.streamer.onboarding?.channelUrl || "—"}</a></div></div>
                  <div><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Age / channel-ownership</span><div data-testid="review-age">{reviewOpen.streamer.onboarding?.ageConfirmed ? <Pill tone="ok">confirmed</Pill> : <Pill tone="danger">NOT confirmed</Pill>}</div></div>
                  <div><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Verification status</span><div data-testid="review-status"><Pill tone={reviewOpen.streamer.verificationStatus === "verified" ? "ok" : reviewOpen.streamer.verificationStatus === "rejected" ? "danger" : "warn"}>{reviewOpen.streamer.verificationStatus}</Pill></div></div>
                  <div className="col-span-2"><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Affiliate deals</span>
                    <div className="mt-1 flex flex-wrap gap-2" data-testid="review-affiliates">
                      {(reviewOpen.streamer.onboarding?.affiliateCasinoIds || []).map((cid) => {
                        const cname = (casinos.find((c) => c.id === cid) || {}).name || cid;
                        return <Pill key={cid} tone="default">{cname}</Pill>;
                      })}
                      {reviewOpen.streamer.onboarding?.affiliateCasinoOther && <Pill tone="default">Other: {reviewOpen.streamer.onboarding.affiliateCasinoOther}</Pill>}
                      {(reviewOpen.streamer.onboarding?.affiliateCasinoIds || []).length === 0 && !reviewOpen.streamer.onboarding?.affiliateCasinoOther && <span className="text-[11px] font-mono text-[#F26D6D]">— none —</span>}
                    </div>
                  </div>
                  <div className="col-span-2"><span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Enabled verticals (read-only here)</span>
                    <div className="mt-1 flex flex-wrap gap-2" data-testid="review-enabled">
                      {(reviewOpen.streamer.enabledVerticals || []).map((v) => <Pill key={v} tone="ok">{v}</Pill>)}
                      {(reviewOpen.streamer.enabledVerticals || []).length === 0 && <span className="text-[11px] font-mono text-[#8B97AE]">none — verification does NOT auto-enable</span>}
                    </div>
                  </div>
                </div>
                <div className="border border-[#F5B841]/40 bg-[#F5B841]/5 p-4 space-y-3" data-testid="review-enablement-section">
                  <div className="text-xs font-mono uppercase tracking-widest text-[#F5B841]">Marketplace access (enable verticals)</div>
                  <p className="text-[11px] text-[#8B97AE] leading-relaxed">
                    Enabling a vertical grants marketplace ACCESS only. It is independent of verification — verifying alone does not grant access, and enabling here does not touch <code className="font-mono">streamerVerified</code>. Enablement is manual and audit-logged.
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    {["casino", "case_opening"].map((v) => {
                      const on = (reviewOpen.streamer.enabledVerticals || []).includes(v);
                      return (
                        <label key={v} className="flex items-center gap-2 cursor-pointer" data-testid={`review-enablement-row-${v}`}>
                          <Switch data-testid={`review-enablement-toggle-${v}`} checked={on} disabled={reviewBusy} onCheckedChange={(next) => toggleReviewEnablement(v, !!next)} />
                          <span className="text-sm text-[#E8ECF4]">{v === "casino" ? "Casino" : "Case-Pack"}</span>
                          <Pill tone={on ? "ok" : "default"}>{on ? "enabled" : "disabled"}</Pill>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Contracts (this streamer)</div>
                  {(() => {
                    const openPlayer = (pid) => { closeReview(); openPlayerDetail(pid); };
                    const nameOf = (arr, id, key) => (arr.find((x) => x.id === id) || {})[key] || "—";
                    const active = reviewRefs.filter((r) => ["active", "closing"].includes(r.status));
                    const past   = reviewRefs.filter((r) => ["completed", "terminated"].includes(r.status));
                    const render = (list, key, emptyLabel) => (
                      <table className="w-full text-[11px] font-mono border-b border-white/5" data-testid={`review-contracts-${key}`}>
                        <thead className="text-[9px] uppercase tracking-widest text-[#8B97AE]"><tr>
                          <th className="text-left p-1">Player</th><th className="text-left p-1">Vertical</th><th className="text-left p-1">Operator</th>
                          <th className="text-left p-1">Signup</th><th className="text-left p-1">End</th>
                          <th className="text-left p-1">ACV</th><th className="text-left p-1">Tracked</th><th className="text-left p-1">Status</th>
                        </tr></thead>
                        <tbody>
                          {list.length === 0 && <tr><td className="p-2 text-[#8B97AE]" colSpan={8} data-testid={`review-contracts-empty-${key}`}>— {emptyLabel} —</td></tr>}
                          {list.map((r) => {
                            const endDate = r.status === "completed" ? (r.completedAt || r.closedAt || "") : r.status === "terminated" ? (r.terminatedAt || (r.termination || {}).at || "") : "";
                            return (
                              <tr key={r.id} className="border-t border-white/5" data-testid={`review-contract-${r.id}`}>
                                <td className="p-1"><button onClick={() => openPlayer(r.playerId)} data-testid={`review-contract-player-${r.id}`} className="text-[#2DD4A7] underline hover:text-white">{nameOf(players, r.playerId, "alias") || r.playerId.slice(0, 8)}</button></td>
                                <td className="p-1">{r.vertical || "casino"}</td>
                                <td className="p-1">{nameOf(casinos, r.casinoId, "name")}</td>
                                <td className="p-1">{(r.signupDate || r.createdAt || "").slice(0, 10)}</td>
                                <td className="p-1">{endDate ? endDate.slice(0, 10) : "—"}</td>
                                <td className="p-1">{r.committedVolume || 0}</td>
                                <td className="p-1">{r.underCodeVolumeCumulative || 0}</td>
                                <td className="p-1"><Pill tone={(r.status === "completed" || r.status === "terminated") ? "ok" : "warn"}>{r.status}</Pill></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                    return (
                      <div className="space-y-2">
                        <div className="text-[9px] font-mono uppercase tracking-widest text-[#2DD4A7]">Active</div>
                        {render(active, "active", "no active contracts")}
                        <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] pt-1">Past</div>
                        {render(past, "past", "no past contracts")}
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Proofs</div>
                  <ul className="space-y-2" data-testid="review-proofs">
                    {(reviewOpen.streamer.onboarding?.proofFileKeys || []).map((k) => (
                      <li key={k} className="border border-white/10 p-2" data-testid={`review-proof-${k.slice(-12)}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs font-mono text-[#E8ECF4]">{k.split("/").pop()}</span>
                          {!proofUrls[k]
                            ? <button data-testid={`review-proof-view-${k.slice(-12)}`} onClick={() => fetchProofUrl(k)} className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-white">View</button>
                            : <a href={proofUrls[k]} target="_blank" rel="noreferrer noopener" data-testid={`review-proof-open-${k.slice(-12)}`} className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] hover:text-white">Open in new tab</a>
                          }
                        </div>
                        {proofUrls[k] && (
                          /\.(jpe?g|png|webp)$/i.test(k)
                            ? <img src={proofUrls[k]} alt="" className="mt-2 max-h-64 object-contain border border-white/10" data-testid={`review-proof-img-${k.slice(-12)}`} />
                            : /\.(mp4|mov|webm)$/i.test(k)
                              ? <video src={proofUrls[k]} controls className="mt-2 max-h-64 w-full border border-white/10" data-testid={`review-proof-vid-${k.slice(-12)}`} />
                              : <div className="mt-2 text-[10px] font-mono uppercase text-[#8B97AE]">Preview available via the "Open in new tab" link.</div>
                        )}
                      </li>
                    ))}
                    {(reviewOpen.streamer.onboarding?.proofFileKeys || []).length === 0 && <li className="text-[11px] font-mono text-[#F26D6D]">— no proofs submitted —</li>}
                  </ul>
                </div>
                {/* Iter 89 — Credit purchases (top-ups) for THIS streamer.
                    Read-only mirror of the TOPUPS tab, scoped to prevent the
                    admin from double-crediting (confirming a top-up already
                    auto-credits availableCredits; GRANT CREDITS is meant for
                    corrections only). No mutations here — confirm/reject
                    stays in the TOPUPS tab. */}
                {(() => {
                  const myTopups = (topups || []).filter((t) => t.streamerId === reviewOpen.streamer.id);
                  const badgeFor = (st) => {
                    const map = {
                      awaiting_transfer: { tone: "warn",   label: "awaiting transfer" },
                      submitted:         { tone: "accent", label: "submitted (needs review)" },
                      confirmed:         { tone: "ok",     label: "confirmed (credited)" },
                      rejected:          { tone: "danger", label: "rejected" },
                    };
                    return map[st] || { tone: "default", label: st || "—" };
                  };
                  const copy = async (text, id) => {
                    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
                    catch { toast.error("Copy failed"); }
                  };
                  const trunc = (s, keep = 10) => (s && s.length > keep * 2 + 3) ? `${s.slice(0, keep)}…${s.slice(-keep)}` : (s || "—");
                  return (
                    <div className="border border-white/10 p-4" data-testid="review-topups-panel">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4A7]">Credit purchases (top-ups)</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B97AE]">Read-only. Confirming a top-up in the TOPUPS tab auto-credits Available.</div>
                      </div>
                      {myTopups.length === 0 ? (
                        <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="review-topups-empty">No credit purchases on record for this streamer.</div>
                      ) : (
                        <div className="border border-white/10 overflow-x-auto">
                          <table className="w-full text-xs" data-testid="review-topups-table">
                            <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                              <tr className="border-b border-white/10">
                                <th className="text-left p-2">#</th>
                                <th className="text-left p-2">Amount</th>
                                <th className="text-left p-2">Currency</th>
                                <th className="text-left p-2">Network</th>
                                <th className="text-left p-2">Status</th>
                                <th className="text-left p-2 min-w-[240px]">Tx hash</th>
                                <th className="text-left p-2">Created</th>
                                <th className="text-left p-2">Confirmed</th>
                                <th className="text-left p-2">Admin note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {myTopups.map((t) => {
                                const b = badgeFor(t.status);
                                const tx = t.txHash || t.txReference || "";
                                return (
                                  <tr key={t.id} className="border-b border-white/5 cursor-pointer hover:bg-white/[0.03]" data-testid={`review-topup-row-${t.id}`} onClick={() => setTopupDetailOpen(t)}>
                                    <td className="p-2 font-mono text-[10px] text-[#8B97AE]" data-testid={`review-topup-seq-${t.id}`}>#{t.seq ?? "—"}</td>
                                    <td className="p-2 font-mono text-[#E8ECF4] tabular-nums" data-testid={`review-topup-amount-${t.id}`}>{Number(t.amount || 0).toLocaleString()}</td>
                                    <td className="p-2 font-mono text-[#8B97AE]">{t.currency || "—"}</td>
                                    <td className="p-2 font-mono text-[#8B97AE]">{t.network || "—"}</td>
                                    <td className="p-2"><Pill tone={b.tone} data-testid={`review-topup-status-${t.id}`}>{b.label}</Pill></td>
                                    <td className="p-2 font-mono text-[#E8ECF4] align-top" data-testid={`review-topup-tx-${t.id}`}>
                                      {tx ? (
                                        <div className="flex items-start gap-2">
                                          <span className="font-mono text-[10px] break-all leading-snug" style={{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{tx}</span>
                                          <button
                                            data-testid={`review-topup-tx-copy-${t.id}`}
                                            onClick={() => copy(tx, t.id)}
                                            className="shrink-0 text-[10px] font-mono text-[#2DD4A7] hover:text-white"
                                            title="Copy tx hash"
                                          >copy</button>
                                        </div>
                                      ) : "—"}
                                    </td>
                                    <td className="p-2 font-mono text-[#8B97AE]" data-testid={`review-topup-created-${t.id}`}>{(t.createdAt || "").slice(0, 16).replace("T", " ")}</td>
                                    <td className="p-2 font-mono" data-testid={`review-topup-confirmed-${t.id}`}>
                                      {t.confirmedAt
                                        ? <>
                                            <div className="text-emerald-400">{(t.confirmedAt || "").slice(0, 16).replace("T", " ")}</div>
                                            <div className="text-[10px] text-[#8B97AE]">by {(t.confirmedBy || "").slice(0, 8) || "—"}</div>
                                          </>
                                        : <span className="text-[#8B97AE]">—</span>}
                                    </td>
                                    <td className="p-2 font-mono text-[#8B97AE]" data-testid={`review-topup-note-${t.id}`}>{t.adminNote || "—"}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {myTopups.some((t) => t.status === "submitted") && (
                        <div className="mt-2 border border-[#F5B841]/60 bg-[#F5B841]/[0.06] p-2 text-[11px] font-mono text-[#F5B841]" data-testid="review-topups-pending-hint">
                          One or more top-ups are awaiting review. Confirm or reject them in the TOPUPS tab — do NOT also use Grant Credits for the same purchase.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Iter 61 — Credits ledger + manual grant/remove.
                    Additional path alongside the top-up flow; every change
                    is delta-based, requires a reason, and audits. */}
                <div className="border border-white/10 p-4" data-testid="review-credits-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#2DD4A7]">Credits ledger</div>
                    <div className="flex items-center gap-3">
                      <button
                        data-testid="credit-history-open"
                        onClick={() => setCreditHistoryStreamer(reviewOpen.streamer)}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] hover:text-[#2DD4A7]"
                      >View history →</button>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B97AE]">Available Credits are a platform entitlement — NOT a crypto balance, NOT redeemable.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-4">
                    {[
                      ["Available", (reviewOpen.streamer.creditLedger?.availableCredits ?? reviewOpen.streamer.creditBalance ?? 0), "review-ledger-available"],
                      ["Reserved",  (reviewOpen.streamer.creditLedger?.reservedCredits  ?? 0),                                    "review-ledger-reserved"],
                      ["Consumed",  (reviewOpen.streamer.creditLedger?.consumedCredits  ?? 0),                                    "review-ledger-consumed"],
                    ].map(([k, v, tid]) => (
                      <div key={k} className="bg-[#0B0E14] p-3" data-testid={tid}>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">{k}</div>
                        <div className="font-display text-xl font-black mt-1 tabular-nums" data-testid={`${tid}-value`}>{Number(v).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-2 mb-2">
                    <div>
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Amount</Label>
                      <Input
                        data-testid="credit-adjust-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={creditDelta}
                        onChange={(e) => setCreditDelta(e.target.value)}
                        placeholder="e.g. 100"
                        className="mt-1 rounded-none bg-transparent border-white/20 h-10 font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Reason / reference (required)</Label>
                      <Input
                        data-testid="credit-adjust-reason"
                        value={creditReason}
                        onChange={(e) => setCreditReason(e.target.value)}
                        placeholder="e.g. tx 0xabc123… · USDT-ERC20  or  correction: duplicate topup rollback"
                        className="mt-1 rounded-none bg-transparent border-white/20 h-10 font-mono text-xs"
                      />
                      <div className="mt-2">
                        <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Grant type</Label>
                        <select
                          data-testid="credit-adjust-grant-type"
                          value={creditGrantType}
                          onChange={(e) => setCreditGrantType(e.target.value)}
                          className="mt-1 w-full rounded-none bg-transparent border border-white/20 h-10 font-mono text-xs px-2 text-[#E8ECF4]"
                        >
                          <option value="adjustment">Adjustment</option>
                          <option value="promotion">Promotion</option>
                          <option value="correction">Correction</option>
                        </select>
                        <div className="text-[9px] font-mono text-[#8B97AE] mt-1">Streamer sees "promotion" grants labelled as Promotional credit; others as Credit adjustment.</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      data-testid="credit-adjust-grant"
                      onClick={() => doCreditAdjust(+1)}
                      disabled={creditBusy || !creditDelta || !(creditReason || "").trim()}
                      className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] h-9 px-4 font-semibold text-xs uppercase tracking-widest disabled:opacity-40"
                    >{creditBusy ? "…" : "Grant credits"}</Button>
                    <Button
                      data-testid="credit-adjust-remove"
                      onClick={() => doCreditAdjust(-1)}
                      disabled={creditBusy || !creditDelta || !(creditReason || "").trim()}
                      className="rounded-none bg-transparent border border-[#F26D6D] text-[#F26D6D] hover:bg-[#F26D6D]/10 h-9 px-4 text-xs uppercase tracking-widest disabled:opacity-40"
                    >{creditBusy ? "…" : "Remove credits"}</Button>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] ml-auto">Every change is audit-logged with the reason.</span>
                  </div>
                  <div className="mt-2 border border-[#F5B841]/50 bg-[#F5B841]/[0.05] p-2 text-[11px] font-mono text-[#F5B841]" data-testid="credit-adjust-caution">
                    Check Credit Purchases above before granting — confirming a top-up already credits Available automatically. Use manual grant only for corrections.
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Admin note (required for Reject / Needs info)</Label>
                  <textarea
                    data-testid="review-note"
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    className="mt-1 w-full rounded-none bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-3"
                    rows={3}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button data-testid="review-approve" disabled={reviewBusy} onClick={() => decide("approved")} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] h-10 font-semibold">Approve</Button>
                  <Button data-testid="review-needs" disabled={reviewBusy} onClick={() => decide("needs_more_info")} className="rounded-none bg-transparent border border-[#F5B841] text-[#F5B841] hover:bg-[#F5B841]/10 h-10">Needs more info</Button>
                  <Button data-testid="review-reject" disabled={reviewBusy} onClick={() => decide("rejected")} className="rounded-none bg-transparent border border-[#F26D6D] text-[#F26D6D] hover:bg-[#F26D6D]/10 h-10">Reject</Button>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] ml-auto">Verification ≠ vertical enablement</span>
                </div>
              </div>
            </div>
          )}


          {enablementHistory && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="enablement-history-modal" onClick={closeEnablementHistory}>
              <div className="bg-[#0B0E14] border border-white/15 max-w-3xl w-full mt-16" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">Enablement history</div>
                    <div className="font-display text-2xl font-black tracking-tighter">{enablementHistory.streamer.displayName}</div>
                  </div>
                  <button data-testid="enablement-history-close" onClick={closeEnablementHistory} className="text-[#8B97AE] hover:text-white text-xs font-mono uppercase tracking-widest">Close</button>
                </div>
                <div className="p-5">
                  {enablementHistory.rows.length === 0 ? (
                    <div className="text-xs font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="enablement-history-empty">No enablement changes yet.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                        <tr className="border-b border-white/10">
                          <th className="text-left p-2">When (UTC)</th>
                          <th className="text-left p-2">Vertical</th>
                          <th className="text-left p-2">Change</th>
                          <th className="text-left p-2">Admin</th>
                          <th className="text-left p-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enablementHistory.rows.map((r) => (
                          <tr key={r.id} className="border-b border-white/5" data-testid={`enablement-history-row-${r.id}`}>
                            <td className="p-2 font-mono text-xs">{(r.createdAt || "").replace("T", " ").slice(0, 19)}</td>
                            <td className="p-2 font-mono text-xs">{r.vertical === "case_opening" ? "Case-Pack" : "Casino"}</td>
                            <td className="p-2 font-mono text-xs">{r.oldValue ? "enabled" : "disabled"} → <span className={r.newValue ? "text-emerald-400" : "text-[#F5B841]"}>{r.newValue ? "enabled" : "disabled"}</span></td>
                            <td className="p-2 font-mono text-xs">{r.adminEmail || r.adminId}</td>
                            <td className="p-2 text-xs text-[#E8ECF4]">{r.reason || <span className="text-[#8B97AE]">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {deleteOpen && (
            <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="streamer-delete-modal" onClick={closeDeleteStreamer}>
              <div className="bg-[#0B0E14] border border-[#F26D6D]/40 max-w-lg w-full mt-16" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#F26D6D]">Danger · Hard delete streamer</div>
                    <div className="font-display text-xl font-black tracking-tighter" data-testid="delete-modal-name">{deleteOpen.streamer.displayName}</div>
                    <div className="font-mono text-[11px] text-[#8B97AE]" data-testid="delete-modal-email">{deleteOpen.streamer.email}</div>
                  </div>
                  <button data-testid="streamer-delete-close" onClick={closeDeleteStreamer} className="text-[#8B97AE] hover:text-white text-xs font-mono uppercase tracking-widest">Close</button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="border border-[#F26D6D]/30 bg-[#F26D6D]/[0.04] p-3 text-xs text-[#E8ECF4] leading-relaxed" data-testid="delete-modal-warning">
                    This is a <strong className="text-[#F26D6D]">GDPR hard-delete</strong>. The streamer record is removed and every proof file they uploaded is genuinely purged from R2 (not just flagged). Audit metadata is retained for compliance.
                    <br /><br />
                    The server refuses (409) if this streamer has any active/closing contract or any pending credit top-up.
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Type the streamer's email to confirm</label>
                    <input
                      data-testid="streamer-delete-confirm-input"
                      type="text"
                      autoFocus
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={deleteOpen.streamer.email}
                      className="mt-2 w-full rounded-none bg-transparent border border-white/20 h-10 px-3 text-sm font-mono focus:border-[#F26D6D] focus:outline-none"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      data-testid="streamer-delete-cancel"
                      onClick={closeDeleteStreamer}
                      className="px-4 h-10 border border-white/20 text-xs font-mono uppercase tracking-widest hover:bg-white/5 transition-colors"
                    >Cancel</button>
                    <button
                      data-testid="streamer-delete-confirm"
                      onClick={doDeleteStreamer}
                      disabled={deleteBusy || (deleteConfirmText || "").trim().toLowerCase() !== String(deleteOpen.streamer.email || "").trim().toLowerCase()}
                      className="px-4 h-10 bg-[#F26D6D] text-[#04150F] font-semibold text-xs font-mono uppercase tracking-widest hover:bg-[#E8ECF4] transition-colors disabled:opacity-40"
                    >{deleteBusy ? "Deleting…" : "Delete streamer"}</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
        ))}

        <TabsContent value="contract-review" className="mt-6 space-y-4">
          <AdminContractReview onAction={loadCounts} />
        </TabsContent>

        <TabsContent value="change-requests" className="mt-6 space-y-4">
          <AdminChangeRequests onAction={loadCounts} />
        </TabsContent>

        <TabsContent value="cancellations" className="mt-6 space-y-4">
          <AdminCancellations onAction={loadCounts} />
        </TabsContent>

        <TabsContent value="contracts" className="mt-6 space-y-4">
          <div className="flex items-center gap-2" data-testid="contracts-iter3-toolbar">
            <Button
              data-testid="contracts-seed-test"
              disabled={contractsBusy}
              onClick={async () => {
                setContractsBusy(true);
                try { const r = await api.post("/admin/contracts/seed-test"); toast.success(`Seeded ${r.data.created?.length || 0} test contracts`); await loadIter3(); loadCounts(); }
                catch (e) { toast.error(apiErrorMessage(e)); }
                finally { setContractsBusy(false); }
              }}
              className="rounded-none bg-transparent border border-[#2DD4A7] text-[#2DD4A7] hover:bg-[#2DD4A7]/10 h-8 font-mono text-[11px] uppercase tracking-widest"
            >Seed test contracts</Button>
            <Button
              data-testid="contracts-cleanup-test"
              disabled={contractsBusy}
              onClick={async () => {
                if (!window.confirm("Delete all is_test iter3 contracts?")) return;
                setContractsBusy(true);
                try { const r = await api.delete("/admin/contracts/test"); toast.success(`Deleted ${r.data.deleted} test contracts`); await loadIter3(); loadCounts(); }
                catch (e) { toast.error(apiErrorMessage(e)); }
                finally { setContractsBusy(false); }
              }}
              className="rounded-none bg-transparent border border-[#F26D6D]/50 text-[#F26D6D] hover:bg-[#F26D6D]/10 h-8 font-mono text-[11px] uppercase tracking-widest"
            >Remove test contracts</Button>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{iter3Contracts.length} contracts · flow=iter3</span>
          </div>
          {/* Iter 130 + 131 — Status-group chips (every state maps into
              exactly one bucket) + free-text search across player alias /
              streamer alias / contract id. Both filter the same list
              client-side; chip narrows to a bucket, search narrows within. */}
          {(() => {
            const CONTRACT_BUCKETS = [
              { key: "all",         label: "All",         states: null },
              { key: "in_progress", label: "In Progress", states: ["draft", "pending_payment", "pending_player_confirmation", "admin_review", "proof_rejected", "change_requested", "cancellation_requested"] },
              { key: "active",      label: "Active",      states: ["active"] },
              { key: "cancelled",   label: "Cancelled",   states: ["cancelled"] },
              { key: "finished",    label: "Finished",    states: ["settled", "finished"] },
              { key: "closed",      label: "Closed",      states: ["declined", "expired"] },
            ];
            const chipTone = (k, isSel) => {
              const base = "px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors";
              if (isSel) {
                const selMap = {
                  all: "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]",
                  in_progress: "bg-[#F5B841] text-[#04150F] border-[#F5B841]",
                  active: "bg-emerald-400 text-[#04150F] border-emerald-400",
                  cancelled: "bg-[#F26D6D] text-white border-[#F26D6D]",
                  finished: "bg-white text-[#04150F] border-white",
                  closed: "bg-[#8B97AE] text-[#04150F] border-[#8B97AE]",
                };
                return `${base} ${selMap[k] || selMap.all}`;
              }
              return `${base} border-white/15 text-[#E8ECF4] hover:bg-white/5`;
            };
            const inBucket = (c, bucket) => bucket.states === null ? true : bucket.states.includes(c.status);
            return (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2" data-testid="contracts-status-chips">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] self-center mr-1">Status</span>
                  {CONTRACT_BUCKETS.map((b) => {
                    const count = b.states === null ? iter3Contracts.length : iter3Contracts.filter((c) => inBucket(c, b)).length;
                    const isSel = contractStatusFilter === b.key;
                    return (
                      <button key={b.key}
                        data-testid={`contracts-chip-${b.key}`}
                        onClick={() => setContractStatusFilter(b.key)}
                        className={chipTone(b.key, isSel)}>
                        {b.label} <span className={`ml-1 tabular-nums ${isSel ? "opacity-90" : "text-[#8B97AE]"}`}>({count})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mr-1">Search</span>
                  <Input
                    data-testid="contracts-search"
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                    placeholder="Player alias, streamer alias, or contract id…"
                    className="rounded-none bg-transparent border-white/20 h-8 max-w-md font-mono text-xs"
                  />
                  {contractSearch && (
                    <button
                      data-testid="contracts-search-clear"
                      onClick={() => setContractSearch("")}
                      className="px-2 h-8 border border-white/15 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#E8ECF4] hover:bg-white/5"
                    >Clear</button>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-contracts-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10">
                <th className="text-left p-3">Player</th><th className="text-left p-3">Streamer</th><th className="text-left p-3">State</th>
                <th className="text-left p-3">Agreed wager · 3-month total</th><th className="text-left p-3">Created</th>
                <th className="text-left p-3">Flags</th>
              </tr></thead>
              <tbody>
                {(() => {
                  const BUCKET_STATES = {
                    all: null,
                    in_progress: ["draft", "pending_payment", "pending_player_confirmation", "admin_review", "proof_rejected", "change_requested", "cancellation_requested"],
                    active: ["active"],
                    cancelled: ["cancelled"],
                    finished: ["settled", "finished"],
                    closed: ["declined", "expired"],
                  };
                  const allowed = BUCKET_STATES[contractStatusFilter];
                  const byBucket = allowed === null ? iter3Contracts : iter3Contracts.filter((c) => allowed.includes(c.status));
                  // Iter 131 — search narrows within the bucket (player alias,
                  // streamer alias, contract id — case-insensitive substring).
                  const q = contractSearch.trim().toLowerCase();
                  const filtered = q ? byBucket.filter((c) => {
                    const pa = (c.player_alias || "").toLowerCase();
                    const sa = (c.streamer_alias || "").toLowerCase();
                    const pid = (c.playerId || "").toLowerCase();
                    const sid = (c.streamerId || "").toLowerCase();
                    const cid = (c.id || "").toLowerCase();
                    return pa.includes(q) || sa.includes(q) || pid.includes(q) || sid.includes(q) || cid.includes(q);
                  }) : byBucket;
                  if (filtered.length === 0) {
                    const emptyMsg = iter3Contracts.length === 0
                      ? "No iter3 contracts. Click \"Seed test contracts\" to create some."
                      : q
                        ? `No contracts match "${contractSearch}" in "${contractStatusFilter}".`
                        : `No contracts in "${contractStatusFilter}".`;
                    return <tr><td className="p-3 text-[#8B97AE]" colSpan={6} data-testid="admin-no-iter3-contracts">{emptyMsg}</td></tr>;
                  }
                  return filtered.map((c) => {
                  const tone = c.status === "active" ? "ok" : c.status === "pending_player_confirmation" ? "warn" : c.status === "pending_payment" ? "warn" : c.status === "cancelled" ? "danger" : c.status === "declined" || c.status === "expired" ? "danger" : "default";
                  const toneCls = { ok: "border-emerald-500/50 text-emerald-400", warn: "border-[#F5B841]/50 text-[#F5B841]", danger: "border-[#F26D6D]/60 text-[#F26D6D]", default: "border-white/20 text-white" }[tone];
                  return (
                    <tr key={c.id} onClick={() => setOpenContractId(c.id)} className="border-b border-white/5 cursor-pointer hover:bg-white/[0.03]" data-testid={`iter3-row-${c.id}`}>
                      <td className="p-3 font-mono text-xs">
                        <div className="text-[#E8ECF4]" data-testid={`iter3-row-player-${c.id}`}>{c.player_alias || (c.playerId || "").slice(0, 8)}</div>
                        <div className="text-[10px] text-[#8B97AE] truncate max-w-[160px]">{c.playerId}</div>
                      </td>
                      <td className="p-3 font-mono text-xs">
                        <div className="text-[#E8ECF4]" data-testid={`iter3-row-streamer-${c.id}`}>{c.streamer_alias || (c.streamerId || "").slice(0, 8)}</div>
                        <div className="text-[10px] text-[#8B97AE] truncate max-w-[160px]">{c.streamerId}</div>
                      </td>
                      <td className="p-3"><span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${toneCls}`} data-testid={`iter3-row-state-${c.id}`}>{c.status}</span></td>
                      <td className="p-3 font-mono">{Number((c.agreed_monthly_volume || 0) * 3).toLocaleString()} USD</td>
                      <td className="p-3 font-mono text-xs text-[#8B97AE]">{(c.createdAt || "").slice(0, 10)}</td>
                      <td className="p-3 space-x-1">
                        {c.is_test && <span className="text-[9px] font-mono uppercase tracking-widest text-[#F5B841] border border-[#F5B841]/50 px-1.5">TEST</span>}
                        {c.refund_due && <span data-testid={`iter3-row-refund-${c.id}`} className="text-[9px] font-mono uppercase tracking-widest text-[#F26D6D] border border-[#F26D6D]/60 px-1.5">REFUND DUE</span>}
                      </td>
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="referrals" className="mt-6 space-y-6">
          <div className="flex items-center gap-2" data-testid="refs-group-toolbar">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Contracts</span>
            {[
              ["active",  "Active (active+closing)"],
              ["past",    "Past (completed+terminated)"],
              ["flagged", "Flagged"],
            ].map(([v, l]) => (
              <button key={v} data-testid={`refs-group-${v}`} onClick={() => setRefsGroup(v)}
                className={`px-3 h-8 border font-mono text-[10px] uppercase tracking-widest transition-colors ${refsGroup === v ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]" : "border-white/15 text-[#E8ECF4] hover:bg-white/5"}`}>{l}</button>
            ))}
          </div>
          {(() => {
            const inGroup = (r) => {
              if (refsGroup === "active") return ["active", "closing"].includes(r.status);
              if (refsGroup === "past") return ["completed", "terminated"].includes(r.status);
              return r.status === "flagged";
            };
            const list = refs.filter(inGroup);
            const nameOf = (arr, id, key) => (arr.find((x) => x.id === id) || {})[key] || "—";
            const openPlayer = (pid) => { openPlayerDetail(pid); };
            return (
              <div className="border border-white/15 overflow-x-auto" data-testid={`refs-list-${refsGroup}`}>
                <table className="w-full text-xs">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10">
                    <th className="text-left p-2">Player</th><th className="text-left p-2">Streamer</th><th className="text-left p-2">Vertical</th><th className="text-left p-2">Operator</th>
                    <th className="text-left p-2">Signup</th><th className="text-left p-2">End</th>
                    <th className="text-left p-2">ACV / Committed</th><th className="text-left p-2">Tracked</th>
                    <th className="text-left p-2">Commission</th><th className="text-left p-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {list.length === 0 && <tr><td className="p-3 text-[#8B97AE] text-[11px] font-mono" colSpan={10} data-testid={`refs-empty-${refsGroup}`}>— no contracts in this group —</td></tr>}
                    {list.map((r) => {
                      const endDate = r.status === "completed" ? (r.completedAt || r.closedAt || "") : r.status === "terminated" ? (r.terminatedAt || (r.termination || {}).at || "") : "";
                      return (
                        <tr key={r.id} className="border-b border-white/5 font-mono" data-testid={`refs-row-${r.id}`}>
                          <td className="p-2"><button data-testid={`refs-player-${r.id}`} onClick={() => openPlayer(r.playerId)} className="text-[#2DD4A7] hover:text-white underline">{nameOf(players, r.playerId, "alias") || r.playerId.slice(0, 8)}</button></td>
                          <td className="p-2">{nameOf(streamers, r.streamerId, "displayName")}</td>
                          <td className="p-2">{r.vertical || "casino"}</td>
                          <td className="p-2">{nameOf(casinos, r.casinoId, "name")}</td>
                          <td className="p-2">{(r.signupDate || r.createdAt || "").slice(0, 10)}</td>
                          <td className="p-2">{endDate ? endDate.slice(0, 10) : "—"}</td>
                          <td className="p-2">{r.committedVolume || 0}</td>
                          <td className="p-2">{r.underCodeVolumeCumulative || 0}</td>
                          <td className="p-2">{r.creditCost != null ? r.creditCost : "—"}</td>
                          <td className="p-2"><Pill tone={r.status === "flagged" ? "danger" : (r.status === "completed" || r.status === "terminated") ? "ok" : "warn"}>{r.status}</Pill></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <div className="border border-white/15 p-6">
            <h3 className="font-display font-bold mb-4">New referral</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={newRef.playerId} onValueChange={(v) => setNewRef({ ...newRef, playerId: v })}>
                <SelectTrigger data-testid="ref-player" className="rounded-none bg-transparent border-white/20 h-10"><SelectValue placeholder="Player" /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.alias}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={newRef.streamerId} onValueChange={(v) => setNewRef({ ...newRef, streamerId: v })}>
                <SelectTrigger data-testid="ref-streamer" className="rounded-none bg-transparent border-white/20 h-10"><SelectValue placeholder="Streamer" /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">{streamers.map((s) => <SelectItem key={s.id} value={s.id}>{s.displayName}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={newRef.casinoId} onValueChange={(v) => setNewRef({ ...newRef, casinoId: v })}>
                <SelectTrigger data-testid="ref-casino" className="rounded-none bg-transparent border-white/20 h-10"><SelectValue placeholder="Casino" /></SelectTrigger>
                <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-white">{casinos.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="ref-code" placeholder="Streamer code" value={newRef.streamerCode} onChange={(e) => setNewRef({ ...newRef, streamerCode: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
              <Input data-testid="ref-signup" type="date" value={newRef.signupDate} onChange={(e) => setNewRef({ ...newRef, signupDate: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
              <Input data-testid="ref-start" type="number" placeholder="Under-code wager start" value={newRef.underCodeWagerStart} onChange={(e) => setNewRef({ ...newRef, underCodeWagerStart: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
              <Input data-testid="ref-committed" type="number" placeholder="Committed volume" value={newRef.committedVolume} onChange={(e) => setNewRef({ ...newRef, committedVolume: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
              <Input data-testid="ref-refvol" type="number" placeholder="Reference volume (info only)" value={newRef.referenceVolume} onChange={(e) => setNewRef({ ...newRef, referenceVolume: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
              <label className="flex items-center gap-2 text-sm text-[#8B97AE]">
                <Checkbox data-testid="ref-existing" checked={newRef.wasExistingCustomer} onCheckedChange={(v) => setNewRef({ ...newRef, wasExistingCustomer: !!v })} className="rounded-none border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-white" />
                Was existing customer (auto-flag not billable)
              </label>
            </div>
            <Button data-testid="ref-add" onClick={addRef} className="mt-4 rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors">Create referral</Button>
          </div>

          <div className="border border-white/15 overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-referrals-table">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10">
                <th className="text-left p-2">Signup</th><th className="text-left p-2">Start</th><th className="text-left p-2">30d</th><th className="text-left p-2">90d</th><th className="text-left p-2">180d</th>
                <th className="text-left p-2">Cumulative</th><th className="text-left p-2">Price %</th><th className="text-left p-2">Cost</th><th className="text-left p-2">Shortfall</th><th className="text-left p-2">Money-back</th><th className="text-left p-2">Loyalty</th><th className="text-left p-2">Status</th>
              </tr></thead>
              <tbody>
                {refs.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 font-mono">
                    <td className="p-2">{(r.signupDate || "").slice(0, 10)}</td>
                    <td className="p-2">{r.underCodeWagerStart}</td>
                    <td className="p-2"><Input data-testid={`ref-w30-${r.id}`} defaultValue={r.underCodeWager30 ?? ""} onBlur={(e) => updateRefField(r.id, { underCodeWager30: e.target.value })} className="rounded-none bg-transparent border-white/20 h-8 w-24" /></td>
                    <td className="p-2"><Input data-testid={`ref-w90-${r.id}`} defaultValue={r.underCodeWager90 ?? ""} onBlur={(e) => updateRefField(r.id, { underCodeWager90: e.target.value })} className="rounded-none bg-transparent border-white/20 h-8 w-24" /></td>
                    <td className="p-2"><Input data-testid={`ref-w180-${r.id}`} defaultValue={r.underCodeWager180 ?? ""} onBlur={(e) => updateRefField(r.id, { underCodeWager180: e.target.value })} className="rounded-none bg-transparent border-white/20 h-8 w-24" /></td>
                    <td className="p-2">{r.underCodeVolumeCumulative}</td>
                    <td className="p-2">{r.creditPricePct != null ? (r.creditPricePct * 100).toFixed(2) + "%" : "—"}</td>
                    <td className="p-2">{r.creditCost}</td>
                    <td className="p-2">{r.shortfall}</td>
                    <td className="p-2">{r.moneyBackCredits}</td>
                    <td className="p-2">{r.loyaltyRebateDue}</td>
                    <td className="p-2"><Pill tone={r.status === "flagged" ? "danger" : r.status === "completed" ? "ok" : "warn"}>{r.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="casinos" className="mt-6 space-y-6">
          <div className="border border-white/15 p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input data-testid="casino-name" placeholder="Name" value={newCasino.name} onChange={(e) => setNewCasino({ ...newCasino, name: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
            <Input data-testid="casino-notes" placeholder="Notes" value={newCasino.notes} onChange={(e) => setNewCasino({ ...newCasino, notes: e.target.value })} className="rounded-none bg-transparent border-white/20 h-10" />
            <Button data-testid="casino-add" onClick={addCasino} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 font-semibold transition-colors">{t("common.add")}</Button>
          </div>
          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-casinos-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10"><th className="text-left p-3">Name</th><th className="text-left p-3">Notes</th><th className="text-left p-3">Active</th><th className="text-left p-3"></th></tr></thead>
              <tbody>
                {casinos.map((c) => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="p-3">{c.name}</td><td className="p-3 text-[#8B97AE] text-xs">{c.notes}</td><td className="p-3">{c.active ? "yes" : "no"}</td>
                    <td className="p-3"><button data-testid={`casino-delete-${c.id}`} onClick={() => deleteCasino(c.id)} className="text-[#F26D6D] hover:text-[#E8ECF4] transition-colors text-xs font-mono uppercase">{t("dash.delete")}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="tiers" className="mt-6 space-y-6">
          {/* Iter 78 — Rewired to the REAL commissionTierConfig (per vertical, versioned).
              The legacy `pricingTiers` (flat, no vertical) is retired. */}
          <div className="border border-white/15 p-4 flex items-center gap-3" data-testid="tiers-header">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Vertical</div>
            <div className="flex gap-2">
              {["casino", "case_opening"].map((v) => (
                <button
                  key={v}
                  data-testid={`tiers-vertical-${v}`}
                  onClick={() => setTiersVertical(v)}
                  className={`px-3 h-8 border font-mono text-[11px] uppercase tracking-widest transition-colors ${
                    tiersVertical === v
                      ? "border-[#2DD4A7] text-[#2DD4A7] bg-[#2DD4A7]/10"
                      : "border-white/20 text-[#8B97AE] hover:text-[#E8ECF4]"
                  }`}
                >{v === "casino" ? "Casino" : "Case"}</button>
              ))}
            </div>
            {currentTierCfg && (
              <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="tiers-meta">
                v{currentTierCfg.version} · {currentThresholds.length} tiers · updated {String(currentTierCfg.updatedAt || "").slice(0, 10)} by {String(currentTierCfg.updatedBy || "—")}
              </div>
            )}
          </div>

          {!tiersMonotonic && (
            <div className="border border-[#F5B841]/50 bg-[#F5B841]/10 px-3 py-2 text-xs text-[#F5B841] font-mono" data-testid="tiers-monotonic-warn">
              ⚠ Rates are not degressive (non-increasing). The curve must fall as volume rises.
            </div>
          )}

          {/* Bulk import (paste `volume,rate` per line — replaces the whole table for this vertical). */}
          <details className="border border-white/15 p-4" data-testid="tiers-bulk">
            <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
              Bulk import — replace {tiersVertical} table from pasted lines
            </summary>
            <div className="mt-3 space-y-3">
              <textarea
                data-testid="tiers-bulk-text"
                className="w-full h-40 rounded-none bg-transparent border border-white/20 p-2 font-mono text-xs text-[#E8ECF4]"
                placeholder={`# Paste one row per line, "minVolume, rate"\n2500, 0.02\n5000, 0.02\n...`}
                value={tiersBulkText}
                onChange={(e) => { setTiersBulkText(e.target.value); setTiersBulkPreview(null); }}
              />
              <div className="flex items-center gap-2">
                <Button data-testid="tiers-bulk-preview" onClick={parseBulk} className="rounded-none bg-transparent border border-white/30 text-[#E8ECF4] hover:bg-white/5 h-9 font-mono text-[11px] uppercase tracking-widest">Preview</Button>
                {tiersBulkPreview?.rows && (
                  <Button
                    data-testid="tiers-bulk-apply"
                    onClick={applyBulk}
                    disabled={tiersBusy}
                    className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-9 font-mono text-[11px] uppercase tracking-widest"
                  >Apply — replace {tiersVertical} ({tiersBulkPreview.rows.length} rows)</Button>
                )}
              </div>
              {tiersBulkPreview?.error && (
                <div className="text-xs text-[#F26D6D] font-mono" data-testid="tiers-bulk-error">{tiersBulkPreview.error}</div>
              )}
              {tiersBulkPreview?.rows && (
                <div className="border border-white/10 max-h-56 overflow-auto" data-testid="tiers-bulk-preview-table">
                  {!tiersBulkPreview.monotonic && (
                    <div className="px-3 py-2 border-b border-[#F5B841]/40 bg-[#F5B841]/10 text-[11px] text-[#F5B841] font-mono">⚠ Preview is not monotonic-degressive.</div>
                  )}
                  <table className="w-full text-xs font-mono">
                    <thead className="text-[10px] uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10"><th className="text-left p-2">minVolume</th><th className="text-left p-2">rate</th><th className="text-left p-2">credit-cost @ minVolume</th></tr></thead>
                    <tbody>
                      {tiersBulkPreview.rows.map((r, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="p-2">{Number(r.minVolume).toLocaleString()}</td>
                          <td className="p-2">{(r.rate * 100).toFixed(2)}%</td>
                          <td className="p-2 text-[#8B97AE]">{Math.round(r.minVolume * r.rate).toLocaleString()} cr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </details>

          {/* Add single row */}
          <div className="border border-white/15 p-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              data-testid="tier-volume"
              type="number"
              placeholder="Min volume (e.g. 30000)"
              value={tiersDraft.minVolume}
              onChange={(e) => setTiersDraft({ ...tiersDraft, minVolume: e.target.value })}
              className="rounded-none bg-transparent border-white/20 h-10"
            />
            <Input
              data-testid="tier-pct"
              type="number"
              step="0.0001"
              placeholder="Rate decimal (e.g. 0.019)"
              value={tiersDraft.rate}
              onChange={(e) => setTiersDraft({ ...tiersDraft, rate: e.target.value })}
              className="rounded-none bg-transparent border-white/20 h-10"
            />
            <Button data-testid="tier-add" onClick={addTierRow} disabled={tiersBusy} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 font-semibold transition-colors">
              {t("common.add")}
            </Button>
          </div>

          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-tiers-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
                <tr className="border-b border-white/10">
                  <th className="text-left p-3">Min volume</th>
                  <th className="text-left p-3">Rate %</th>
                  <th className="text-left p-3">Credit-cost @ min</th>
                  <th className="text-left p-3"></th>
                </tr>
              </thead>
              <tbody>
                {currentThresholds.length === 0 && (
                  <tr><td className="p-3 text-[#8B97AE]" colSpan={4}>No tiers configured for this vertical.</td></tr>
                )}
                {currentThresholds.map((tr, i) => (
                  <tr key={`${tr.minVolume}-${i}`} className="border-b border-white/5" data-testid={`tiers-row-${tiersVertical}-${i}`}>
                    <td className="p-3 font-mono">{Number(tr.minVolume).toLocaleString()}</td>
                    <td className="p-3 font-mono">{(Number(tr.rate) * 100).toFixed(2)}%</td>
                    <td className="p-3 font-mono text-[#8B97AE]">{Math.round(Number(tr.minVolume) * Number(tr.rate)).toLocaleString()} cr</td>
                    <td className="p-3">
                      <button
                        data-testid={`tier-delete-${tiersVertical}-${i}`}
                        onClick={() => removeTierRow(i)}
                        disabled={tiersBusy}
                        className="text-[#F26D6D] hover:text-[#E8ECF4] transition-colors text-xs font-mono uppercase disabled:opacity-40"
                      >{t("dash.delete")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {["payouts-pending", "payouts-done"].map((_scopeV) => (
        <TabsContent key={_scopeV} value={_scopeV} className="mt-6">
          {_scopeV === "payouts-pending" && (
            <div className="mb-3 flex items-center gap-2" data-testid="payouts-test-toolbar">
              <Button
                data-testid="payouts-seed-test"
                disabled={seedBusy}
                onClick={async () => {
                  setSeedBusy(true);
                  try { const r = await api.post("/admin/payouts/seed-test"); toast.success(`Seeded ${r.data.created.length} test payouts`); await load(); }
                  catch (e) { toast.error(apiErrorMessage(e)); }
                  finally { setSeedBusy(false); }
                }}
                className="rounded-none bg-transparent border border-[#2DD4A7] text-[#2DD4A7] hover:bg-[#2DD4A7]/10 h-8 font-mono text-[11px] uppercase tracking-widest"
              >Seed 3 test payouts</Button>
              <Button
                data-testid="payouts-cleanup-test"
                disabled={seedBusy}
                onClick={async () => {
                  if (!window.confirm("Delete all is_test payouts?")) return;
                  setSeedBusy(true);
                  try { const r = await api.delete("/admin/payouts/test"); toast.success(`Deleted ${r.data.deleted} test payouts`); await load(); }
                  catch (e) { toast.error(apiErrorMessage(e)); }
                  finally { setSeedBusy(false); }
                }}
                className="rounded-none bg-transparent border border-[#F26D6D]/50 text-[#F26D6D] hover:bg-[#F26D6D]/10 h-8 font-mono text-[11px] uppercase tracking-widest"
              >Remove test payouts</Button>
            </div>
          )}
          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-payouts-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10"><th className="text-left p-3">Type</th><th className="text-left p-3">Recipient</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Currency</th><th className="text-left p-3">Wallet</th><th className="text-left p-3">Referral</th><th className="text-left p-3">Auto</th><th className="text-left p-3">Tx ref</th><th className="text-left p-3">Status</th><th className="text-left p-3">Actions</th></tr></thead>
              <tbody>
                {(() => {
                  const scopedPayouts = payouts.filter((p) => {
                    const done = p.status === "paid" || p.status === "failed";
                    return _scopeV === "payouts-done" ? done : !done;
                  });
                  if (scopedPayouts.length === 0) return <tr><td className="p-3 text-[#8B97AE]" colSpan={10} data-testid={`admin-no-${_scopeV}`}>{_scopeV === "payouts-pending" ? "No pending payouts." : "No completed payouts yet."}</td></tr>;
                  return scopedPayouts.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => setOpenPayoutId(p.id)}
                    className="border-b border-white/5 cursor-pointer hover:bg-white/[0.03]"
                    data-testid={`payout-row-${p.id}`}
                  >
                    <td className="p-3">{p.type}{p.is_test && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-[#F5B841] border border-[#F5B841]/50 px-1.5">TEST</span>}</td>
                    <td className="p-3 font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        data-testid={`payout-row-name-${p.id}`}
                        onClick={() => setOpenRecipientForPayoutId(p.id)}
                        disabled={!p.playerId && !p.streamerId}
                        className="text-left text-[#2DD4A7] hover:text-white underline disabled:text-[#8B97AE] disabled:no-underline disabled:cursor-default"
                      >{p.recipient_name || (p.playerId || p.streamerId || "—").slice(0, 8)}</button>
                      <div className="text-[10px] text-[#8B97AE] truncate max-w-[160px]">{p.playerId || p.streamerId || "—"}</div>
                    </td>
                    <td className="p-3 font-mono">{p.amount}</td><td className="p-3">{p.currency}</td>
                    <td className="p-3 font-mono text-xs max-w-[160px] truncate">{p.walletAddressSnapshot || "—"}</td>
                    <td className="p-3 font-mono text-xs">{p.referralId ? p.referralId.slice(0, 8) : "—"}</td>
                    <td className="p-3">{p.autoCreated ? <Pill tone="ok">auto</Pill> : "—"}</td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Input data-testid={`payout-tx-${p.id}`} defaultValue={p.txReference || ""} placeholder="tx hash" disabled={p.status === "paid"}
                             onBlur={(e) => { if (e.target.value !== (p.txReference || "")) api.patch(`/payouts/${p.id}`, { txReference: e.target.value }).then(() => load()).catch((err) => toast.error(apiErrorMessage(err))); }}
                             className="rounded-none bg-transparent border-white/20 h-8 w-40 font-mono text-xs" />
                    </td>
                    <td className="p-3"><Pill tone={p.status === "paid" ? "ok" : p.status === "failed" ? "danger" : "warn"}>{p.status}</Pill></td>
                    <td className="p-3 space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`payout-paid-${p.id}`} disabled={p.status === "paid"} onClick={async () => { try { await api.patch(`/payouts/${p.id}`, { status: "paid" }); toast.success("Marked paid"); load(); } catch (e) { toast.error(apiErrorMessage(e)); } }} className="text-emerald-400 hover:text-white transition-colors text-xs font-mono uppercase disabled:opacity-40">Paid</button>
                      <button data-testid={`payout-failed-${p.id}`} disabled={p.status === "paid"} onClick={async () => { try { await api.patch(`/payouts/${p.id}`, { status: "failed" }); load(); } catch (e) { toast.error(apiErrorMessage(e)); } }} className="text-[#F26D6D] hover:text-[#E8ECF4] transition-colors text-xs font-mono uppercase disabled:opacity-40">Fail</button>
                    </td>
                  </tr>
                ));
                })()}
              </tbody>
            </table>
          </div>
        </TabsContent>
        ))}

        <TabsContent value="support" className="mt-6">
          <SupportTickets admin />
        </TabsContent>

        <TabsContent value="audit" className="mt-6 space-y-6">
          <div className="border border-white/8 p-4 bg-[#151B28]/60" data-testid="cmld-maintenance-panel">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">CMLD · Nightly maintenance</div>
                <div className="text-xs text-[#8B97AE] mt-1 max-w-xl">
                  Runs the same three steps the 02:15 UTC cron does: full <b>auditLog archive</b> (no-TTL),
                  per-owner <b>reconciliation</b> (seeds opening_balance when missing), and the <b>stuck-reserve</b> check.
                  Writes an audit row and shows the result lines below. Safe to run any time — nothing is destructive.
                </div>
              </div>
              <Button
                data-testid="cmld-run-maintenance-btn"
                onClick={runMaintenance}
                disabled={maintBusy}
                className="rounded-none bg-[#F5B841] text-[#0B0F1A] hover:bg-[#F5B841]/90 font-semibold shrink-0"
              >
                {maintBusy ? "Running…" : "Run maintenance now"}
              </Button>
            </div>
            {maintResult && !maintResult.error && (
              <div className="mt-4 font-mono text-[11px] space-y-1" data-testid="cmld-maintenance-result">
                <div className="text-[#8B97AE]">
                  <span className="text-[#2DD4A7]">archive</span>{" "}
                  runId=<span className="text-white">{maintResult.runId}</span>{" "}
                  copied=<span className="text-white">{maintResult.archive?.copied ?? "—"}</span>{" "}
                  already=<span className="text-white">{maintResult.archive?.already ?? "—"}</span>
                </div>
                <div className="text-[#8B97AE]">
                  <span className="text-[#2DD4A7]">reconcile</span>{" "}
                  streamers=<span className="text-white">{maintResult.reconcile?.streamers ?? "—"}</span>{" "}
                  openingSeeded=<span className="text-white">{maintResult.reconcile?.openingSeeded ?? "—"}</span>{" "}
                  ok=<span className="text-white">{maintResult.reconcile?.ok ?? "—"}</span>{" "}
                  mismatch=<span className={(maintResult.reconcile?.mismatch || 0) > 0 ? "text-[#F5B841]" : "text-white"}>{maintResult.reconcile?.mismatch ?? "—"}</span>
                </div>
                <div className="text-[#8B97AE]">
                  <span className="text-[#2DD4A7]">stuck_reserve</span>{" "}
                  streamers=<span className="text-white">{maintResult.stuck_reserve?.streamers ?? "—"}</span>{" "}
                  flagged_streamers=<span className={(maintResult.stuck_reserve?.flagged_streamers || 0) > 0 ? "text-[#F5B841]" : "text-white"}>{maintResult.stuck_reserve?.flagged_streamers ?? "—"}</span>{" "}
                  flagged_terminal_contracts=<span className={(maintResult.stuck_reserve?.flagged_terminal_contracts || 0) > 0 ? "text-[#F5B841]" : "text-white"}>{maintResult.stuck_reserve?.flagged_terminal_contracts ?? "—"}</span>
                </div>
                {maintResult.errors?.length > 0 && (
                  <div className="text-red-400">errors: {JSON.stringify(maintResult.errors)}</div>
                )}
              </div>
            )}
            {maintResult?.error && (
              <div className="mt-4 font-mono text-[11px] text-red-400" data-testid="cmld-maintenance-error">
                {maintResult.error}
              </div>
            )}
          </div>

          <div className="border border-white/8 p-4 bg-[#151B28]/60" data-testid="audit-retention-notice">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">Retention</div>
            <div className="text-xs text-[#8B97AE] mt-1">Audit entries and IPs auto-expire after 90 days. Recent lockouts below are read-only — export of raw IP lists is disabled.</div>
          </div>

          <div className="border border-white/8" data-testid="admin-lockouts-panel">
            <div className="p-3 border-b border-white/8 flex items-center justify-between">
              <div className="font-display font-bold text-sm">Recent lockouts</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">read-only · no export</div>
            </div>
            <table className="w-full text-xs" data-testid="admin-lockouts-table">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/8"><th className="text-left p-3">Time</th><th className="text-left p-3">Account</th><th className="text-left p-3">IP</th><th className="text-left p-3">Email sent</th></tr></thead>
              <tbody>
                {auditEntries.filter((e) => e.action === "auth.lockout_notified").slice(0, 20).length === 0 && (
                  <tr><td colSpan={4} className="p-3 text-[#8B97AE]" data-testid="admin-no-lockouts">No lockouts recorded.</td></tr>
                )}
                {auditEntries.filter((e) => e.action === "auth.lockout_notified").slice(0, 20).map((e) => (
                  <tr key={e.id} className="border-b border-white/5 font-mono">
                    <td className="p-3 text-[10px] whitespace-nowrap">{(e.createdAt || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="p-3 text-[11px]">{e.resourceType}/{e.resourceId ? e.resourceId.slice(0, 8) : "—"}</td>
                    <td className="p-3 text-[10px] text-[#8B97AE]">{e.ip || "—"}</td>
                    <td className="p-3">{e.extra?.emailSent ? <Pill tone="ok">sent</Pill> : <Pill tone="warn">throttled</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border border-white/8">
            <table className="w-full text-xs" data-testid="admin-audit-table">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/8"><th className="text-left p-3">Time</th><th className="text-left p-3">Actor</th><th className="text-left p-3">Action</th><th className="text-left p-3">Resource</th><th className="text-left p-3">IP</th><th className="text-left p-3">Extra</th></tr></thead>
              <tbody>
                {auditEntries.length === 0 && <tr><td className="p-3 text-[#8B97AE]" colSpan={6} data-testid="admin-no-audit">No audit entries yet.</td></tr>}
                {auditEntries.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 align-top">
                    <td className="p-3 font-mono text-[10px] whitespace-nowrap">{(e.createdAt || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="p-3 font-mono text-[11px]">{e.actorEmail || (e.actorRole === "system" ? "system" : "—")} <span className="text-[#8B97AE]">({e.actorRole || "?"})</span></td>
                    <td className="p-3 font-mono text-[#2DD4A7]">{e.action}</td>
                    <td className="p-3 font-mono text-[11px]">{e.resourceType}/{e.resourceId ? e.resourceId.slice(0, 8) : "—"}</td>
                    <td className="p-3 font-mono text-[10px] text-[#8B97AE]">{e.ip || "—"}</td>
                    <td className="p-3 text-[10px] text-[#8B97AE] max-w-[280px] break-words">{e.extra && Object.keys(e.extra).length ? JSON.stringify(e.extra) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <div className="border border-white/8 p-6 space-y-6 max-w-3xl">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">Loyalty rebate %</Label>
              <Input data-testid="cfg-loyalty" defaultValue={cfg.loyaltyRebatePct ?? 0.10} step="0.01" type="number" onBlur={(e) => saveLoyalty(e.target.value)} className="rounded-none bg-transparent border-white/20 h-10 mt-2 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE] mb-2 block">Crypto payment addresses (for streamer credit purchases)</Label>
              <div className="space-y-2">
                {Object.entries(cfg.depositAddresses || cfg.depositWallets || {}).map(([net, addr]) => (
                  <div key={net} className="flex items-center gap-2">
                    <span className="font-mono text-xs uppercase text-[#8B97AE] w-24 shrink-0">{net}</span>
                    <Input data-testid={`cfg-address-${net}`} defaultValue={addr} onBlur={(e) => saveDepositAddress(net, e.target.value)} className="rounded-none bg-transparent border-white/20 h-9 font-mono text-xs" />
                  </div>
                ))}
              </div>
            </div>
            {/* Iter 88 — VG-owned INBOUND receiving wallets, keyed by (coin, chain). Read-only. Env/settings-driven. */}
            <div data-testid="cfg-receiving-wallets">
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE] mb-2 block">Receiving wallets (VG-owned inbound addresses)</Label>
              <p className="text-xs text-[#8B97AE] mb-3">Public receiving addresses only. Configured via server settings — read-only here. Payout/top-up selection is by (coin · chain) so shared addresses stay disambiguated.</p>
              {receivingWallets.length === 0 ? (
                <div className="text-xs text-[#8B97AE]" data-testid="cfg-receiving-empty">No receiving wallets configured.</div>
              ) : (
                <div className="border border-white/10">
                  <table className="w-full text-xs" data-testid="cfg-receiving-table">
                    <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                      <tr className="border-b border-white/10">
                        <th className="text-left p-2">Coin</th>
                        <th className="text-left p-2">Chain</th>
                        <th className="text-left p-2">Address</th>
                        <th className="text-left p-2">Tag</th>
                        <th className="text-left p-2">Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivingWallets.map((w, i) => (
                        <tr key={`${w.coin}-${w.chain}-${i}`} className="border-b border-white/5" data-testid={`cfg-receiving-row-${w.coin}-${w.chain}`}>
                          <td className="p-2 font-mono uppercase text-[#E8ECF4]">{w.coin}</td>
                          <td className="p-2 font-mono text-[#8B97AE]">{w.chain}</td>
                          <td className="p-2 font-mono text-[#E8ECF4] break-all">{w.address}</td>
                          <td className="p-2 font-mono text-[#8B97AE]">{w.destination_tag ?? "—"}</td>
                          <td className="p-2 font-mono">{w.is_active
                            ? <span className="inline-flex items-center border border-emerald-500/50 text-emerald-400 px-2 py-0.5 text-[10px] uppercase tracking-widest">active</span>
                            : <span className="inline-flex items-center border border-white/20 text-[#8B97AE] px-2 py-0.5 text-[10px] uppercase tracking-widest">off</span>
                          }</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#8B97AE] mb-2 block">Player wallet networks</Label>
              <p className="text-xs text-[#8B97AE] mb-3">Toggle networks on or off without a deploy. Disabled networks won't appear in player sign-up or wallet-edit forms.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cfg-networks">
                {(cfg.networks || []).map((n) => (
                  <label key={n.value} className="flex items-center justify-between border border-white/8 px-3 py-2">
                    <div>
                      <div className="text-sm">{n.label}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{n.group} · {n.family}</div>
                    </div>
                    <Switch
                      data-testid={`net-toggle-${n.value}`}
                      checked={!!n.enabled}
                      onCheckedChange={(v) => toggleNetwork(n.value, v)}
                      className="data-[state=checked]:bg-[#2DD4A7]"
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="text-xs text-[#8B97AE]">Currencies: {(cfg.currencies || []).join(", ")}</div>
            <div className="text-xs text-[#8B97AE]">Age note: {cfg.minAgeNote}</div>
          </div>

          {/* Iter 179 — Manual one-off ops. Reuses existing sweep endpoints;
              never introduces new server logic. Lives in the config tab
              alongside the danger-zone one-off tools. */}
          <div className="mt-8 border border-white/15 p-6 max-w-3xl space-y-4" data-testid="admin-ops-panel">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] border border-[#2DD4A7]/60 px-2 py-0.5">Ops · one-off</span>
            </div>
            <h3 className="font-display font-bold text-lg text-[#E8ECF4]">Wager-verification reminder</h3>
            <p className="text-xs text-[#8B97AE] leading-relaxed">
              Sends the one-shot reminder email to every player who registered 3+ days ago,
              hasn't submitted their wager verification, and hasn't already been reminded.
              The daily cron fires at 16:00 UTC on its own — this button is a manual trigger
              that uses the same sweep. Dry-run count is fetched first; you confirm before
              real emails are sent. Sent players are marked so they can never receive it twice.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="admin-wager-reminder-run"
                disabled={wagerReminderBusy}
                onClick={runWagerReminder}
                className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#2DD4A7]/80 h-10 px-6 font-mono text-[11px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
              >{wagerReminderBusy ? "Working…" : "Send wager-verification reminder now"}</Button>
              <span className="text-[10px] font-mono text-[#8B97AE]">Reuses the daily cron sweep · at-most-once per player</span>
            </div>
            {wagerReminderResult && (
              <div className="border border-white/15 p-3 text-[11px] font-mono" data-testid="admin-wager-reminder-result">
                <div className="text-[10px] uppercase tracking-widest text-[#8B97AE] mb-2">
                  {wagerReminderResult.dry_run ? "Dry-run result" : "Send result"}
                </div>
                <div className="grid grid-cols-3 gap-4 text-[#E8ECF4]">
                  <div>matched: <span className="text-[#2DD4A7]">{wagerReminderResult.matched ?? 0}</span></div>
                  <div>sent: <span className="text-[#2DD4A7]">{wagerReminderResult.sent ?? 0}</span></div>
                  <div>failed: <span className={(wagerReminderResult.failed ?? 0) > 0 ? "text-[#F26D6D]" : "text-[#8B97AE]"}>{wagerReminderResult.failed ?? 0}</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Iter 134 — DANGER ZONE. Destructive reset of ALL test content
              while keeping admin accounts + config + casinos. Three guards:
              typed confirmation, backend APP_ENV check, admin role. */}
          <div className="mt-8 border-2 border-[#F26D6D]/70 bg-[#F26D6D]/[0.05] p-6 max-w-3xl space-y-4" data-testid="admin-danger-zone">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] border border-[#F26D6D]/60 px-2 py-0.5">Danger zone · preview/dev only</span>
            </div>
            <h3 className="font-display font-bold text-lg text-[#F26D6D]">Reset environment</h3>
            <p className="text-xs text-[#8B97AE] leading-relaxed">
              Wipes ALL test content — every player, streamer, contract, payout, chat, referral, top-up, ticket,
              file (including its object in R2 storage), and audit log entry. <b>Irreversible.</b>
              Admin accounts, casinos, config, coefficient tables, tier tables, receiving wallets, and
              feature-flag env variables are all preserved.
            </p>
            <div className="grid grid-cols-2 gap-4 text-[11px] font-mono">
              <div className="border border-[#F26D6D]/40 p-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#F26D6D]">Will delete</div>
                <ul className="list-disc list-inside text-[#E8ECF4] space-y-0.5">
                  <li>players · streamers · contracts</li>
                  <li>payouts · chats · chatMessages</li>
                  <li>referrals · creditTopups · unlocks</li>
                  <li>tickets · ticketMessages · change-requests</li>
                  <li>files (+ R2 objects)</li>
                  <li>consentLogs · passwordResetTokens</li>
                  <li>loginAttempts · auditLog · misc logs</li>
                </ul>
              </div>
              <div className="border border-[#2DD4A7]/40 p-3 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#2DD4A7]">Will keep</div>
                <ul className="list-disc list-inside text-[#E8ECF4] space-y-0.5">
                  <li>admins (all admin accounts)</li>
                  <li>casinos (whole list)</li>
                  <li>config + pricingTiers</li>
                  <li>commissionTierConfig + history</li>
                  <li>counters</li>
                  <li>env: RECEIVING_WALLETS, coefficients, feature flags, R2 creds</li>
                </ul>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#F26D6D]">Type <span className="font-mono text-[#F26D6D]">RESET</span> (exact caps) to enable the button</Label>
              <Input
                data-testid="admin-reset-confirm-input"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                className="rounded-none bg-transparent border-[#F26D6D]/60 h-10 mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-[#F26D6D]">Reset password</Label>
              <Input
                data-testid="admin-reset-password-input"
                type="password"
                autoComplete="new-password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Enter the reset password"
                className="rounded-none bg-transparent border-[#F26D6D]/60 h-10 mt-2 font-mono text-sm"
              />
              <div className="text-[10px] font-mono text-[#8B97AE] mt-1">
                Set once as <span className="text-[#E8ECF4]">RESET_PASSWORD_HASH</span> in the server .env — see the danger-zone comment in <span className="text-[#E8ECF4]">server.py</span> for the one-line bcrypt helper.
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="admin-reset-run"
                disabled={resetBusy || resetConfirm.trim() !== "RESET" || !resetPassword}
                onClick={runReset}
                className="rounded-none bg-[#F26D6D] text-[#04150F] hover:bg-[#F26D6D]/80 h-10 px-6 font-mono text-[11px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
              >{resetBusy ? "Resetting…" : "Reset environment"}</Button>
              <span className="text-[10px] font-mono text-[#8B97AE]">Server also enforces APP_ENV allow-list, typed confirmation, and the reset-password hash — this button is a UI convenience gate.</span>
            </div>
            {resetResult && (
              <div className="border border-white/15 p-3 text-[11px] font-mono" data-testid="admin-reset-result">
                <div className="text-[10px] uppercase tracking-widest text-[#2DD4A7] mb-2">Reset complete · APP_ENV={resetResult.app_env}</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B97AE] mb-1">Deleted</div>
                    <ul className="text-[#E8ECF4] space-y-0.5">
                      {Object.entries(resetResult.deleted || {}).map(([k, v]) => (
                        <li key={k}>{k}: <span className="text-[#F26D6D]">{v}</span></li>
                      ))}
                      <li>R2 objects: <span className="text-[#F26D6D]">{resetResult.r2_objects_deleted}</span>{resetResult.r2_failure_count > 0 && <span className="ml-2 text-[#F5B841]">({resetResult.r2_failure_count} failed)</span>}</li>
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B97AE] mb-1">Kept</div>
                    <ul className="text-[#E8ECF4] space-y-0.5">
                      {Object.entries(resetResult.kept || {}).map(([k, v]) => (
                        <li key={k}>{k}: <span className="text-[#2DD4A7]">{v}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {["topups-pending", "topups-done"].map((_scopeV) => (
        <TabsContent key={_scopeV} value={_scopeV} className="mt-6">
          <div className="border border-white/15">
            <table className="w-full text-sm" data-testid="admin-topups-table">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]"><tr className="border-b border-white/10"><th className="text-left p-3">#</th><th className="text-left p-3">Date</th><th className="text-left p-3">Streamer</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Currency</th><th className="text-left p-3">Network</th><th className="text-left p-3 min-w-[300px]">Tx hash</th><th className="text-left p-3">From</th><th className="text-left p-3">Status</th><th className="text-left p-3">Actions</th></tr></thead>
              <tbody>
                {(() => {
                  const scopedTopups = topups.filter((tp) => {
                    const done = tp.status === "confirmed" || tp.status === "rejected";
                    return _scopeV === "topups-done" ? done : !done;
                  });
                  if (scopedTopups.length === 0) return <tr><td className="p-3 text-[#8B97AE]" colSpan={10} data-testid={`admin-no-${_scopeV}`}>{_scopeV === "topups-pending" ? "No pending top-ups." : "No resolved top-ups yet."}</td></tr>;
                  return scopedTopups.map((tp) => {
                  const s = streamers.find((x) => x.id === tp.streamerId);
                  const txv = tp.txHash || tp.txReference || "";
                  return (
                    <tr key={tp.id} className="border-b border-white/5 align-top cursor-pointer hover:bg-white/[0.03]" data-testid={`admin-topup-row-${tp.id}`} onClick={() => setTopupDetailOpen(tp)}>
                      <td className="p-3 font-mono text-[10px] text-[#8B97AE]" data-testid={`admin-topup-seq-${tp.id}`}>#{tp.seq ?? "—"}</td>
                      <td className="p-3 font-mono text-xs">{(tp.createdAt || "").slice(0, 10)}</td>
                      <td className="p-3">{s?.displayName || tp.streamerId}</td>
                      <td className="p-3 font-mono">
                        {tp.amount}
                        {typeof tp.grantedAmount === "number" && tp.grantedAmount !== tp.amount && (
                          <div className="text-[10px] font-mono text-[#F5B841]" data-testid={`admin-topup-granted-${tp.id}`}>granted: {tp.grantedAmount}</div>
                        )}
                      </td>
                      <td className="p-3">{tp.currency}</td>
                      <td className="p-3 font-mono text-xs">{tp.network}</td>
                      <td className="p-3 font-mono text-xs align-top" data-testid={`admin-topup-tx-${tp.id}`}>
                        {txv ? (
                          <div className="flex items-start gap-2">
                            <span className="font-mono text-[11px] break-all leading-snug" style={{display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{txv}</span>
                            <button
                              data-testid={`admin-topup-tx-copy-${tp.id}`}
                              onClick={async () => { try { await navigator.clipboard.writeText(txv); toast.success("Copied"); } catch { toast.error("Copy failed"); } }}
                              className="shrink-0 text-[10px] font-mono text-[#2DD4A7] hover:text-white"
                              title="Copy tx hash"
                            >copy</button>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="p-3 font-mono text-xs max-w-[160px] truncate">{tp.fromAddress || tp.fromWallet || "—"}</td>
                      <td className="p-3"><Pill tone={tp.status === "confirmed" ? "ok" : tp.status === "rejected" ? "danger" : "warn"}>{tp.status}</Pill></td>
                      <td className="p-3 space-x-2" onClick={(e) => e.stopPropagation()}>
                        {_scopeV === "topups-pending" ? (
                          <>
                            <button data-testid={`topup-confirm-${tp.id}`} onClick={() => setTopupDetailOpen(tp)} className="text-emerald-400 hover:text-white transition-colors text-xs font-mono uppercase">Confirm</button>
                            <button data-testid={`topup-reject-${tp.id}`} onClick={() => setTopupDetailOpen(tp)} className="text-[#F26D6D] hover:text-[#E8ECF4] transition-colors text-xs font-mono uppercase">Reject</button>
                          </>
                        ) : (
                          <button data-testid={`topup-view-${tp.id}`} onClick={() => setTopupDetailOpen(tp)} className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-white">View</button>
                        )}
                      </td>
                    </tr>
                  );
                });
                })()}
              </tbody>
            </table>
          </div>
        </TabsContent>
        ))}

        <TabsContent value="import" className="mt-6 space-y-6">
          <div className="border border-white/15 p-6">
            <h3 className="font-display font-bold mb-2">CSV bulk import — referral wager checkpoints</h3>
            <p className="text-sm text-[#8B97AE] mb-4 leading-relaxed">
              Columns: <span className="font-mono text-white">referralId, underCodeWagerStart, underCodeWager30, underCodeWager90, underCodeWager180, committedVolume</span>. Only existing referrals are updated. Flagged / existing-customer rows are skipped. <span className="text-[#F5B841]">referenceVolume can never be written via CSV.</span>
            </p>
            <input data-testid="import-file" type="file" accept=".csv" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }} className="block text-sm text-[#8B97AE] file:mr-3 file:border file:border-white/20 file:bg-transparent file:text-white file:px-4 file:py-2 file:font-mono file:text-xs hover:file:bg-white/10" />
            <div className="flex gap-3 mt-4">
              <Button data-testid="import-preview" onClick={previewImport} disabled={importBusy || !importFile} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors">Preview changes</Button>
              {importPreview && <Button data-testid="import-apply" onClick={applyImport} disabled={importBusy} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors">Apply {importPreview.rows.filter((r) => !r.skipped).length} row(s)</Button>}
            </div>
          </div>
          {importPreview && (
            <div className="border border-white/15 p-6" data-testid="import-preview-panel">
              {importPreview.errors?.length > 0 && (
                <div className="mb-4 border border-[#2DD4A7]/40 bg-[#2DD4A7]/5 p-3 text-xs text-[#2DD4A7] font-mono space-y-1" data-testid="import-errors">
                  {importPreview.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                    <tr className="border-b border-white/10"><th className="text-left p-2">Referral</th><th className="text-left p-2">Start</th><th className="text-left p-2">30d</th><th className="text-left p-2">90d</th><th className="text-left p-2">180d</th><th className="text-left p-2">Committed</th><th className="text-left p-2">→ Cumulative</th><th className="text-left p-2">→ Cost</th><th className="text-left p-2">→ Loyalty</th></tr>
                  </thead>
                  <tbody>
                    {importPreview.rows.map((r, i) => r.skipped ? (
                      <tr key={i} className="border-b border-white/5 text-[#8B97AE]"><td className="p-2 font-mono">{r.referralId}</td><td colSpan={8} className="p-2">skipped ({r.reason})</td></tr>
                    ) : (
                      <tr key={i} className="border-b border-white/5 font-mono">
                        <td className="p-2">{r.referralId}</td>
                        <td className="p-2">{r.after.underCodeWagerStart ?? r.before.underCodeWagerStart}</td>
                        <td className="p-2">{r.after.underCodeWager30 ?? "—"}</td>
                        <td className="p-2">{r.after.underCodeWager90 ?? "—"}</td>
                        <td className="p-2">{r.after.underCodeWager180 ?? "—"}</td>
                        <td className="p-2">{r.after.committedVolume ?? r.before.committedVolume}</td>
                        <td className="p-2 text-white">{r.computedAfter.underCodeVolumeCumulative}</td>
                        <td className="p-2 text-white">{r.computedAfter.creditCost}</td>
                        <td className="p-2 text-white">{r.computedAfter.loyaltyRebateDue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Iter 42 — Admin Player Detail modal. Rendered outside <Tabs> so it
          works from the Players table, the Referrals table and the Streamer
          Review modal (all of which call openPlayerDetail). All PII (gaming
          account names, wallet address+network+coin+tag) is admin-only and
          fetched fresh from GET /api/players/{id} on open — same endpoint the
          iter-41 fix hardened to return 403 for streamers. */}
      {playerDetail && (() => {
        const p = playerDetail;
        const vv = p.verticalVerification || {};
        const casinoExplicit = Object.prototype.hasOwnProperty.call(vv, "casino");
        const legacyCasino = !casinoExplicit && p.verificationTier && ["verified", "vip"].includes(p.verificationTier) && p.status === "approved";
        const casinoEffective = casinoExplicit ? !!vv.casino : !!legacyCasino;
        const caseEffective = !!vv.case_opening;
        const casinoBusy = !!pvvBusy[`${p.id}:casino`];
        const caseBusy = !!pvvBusy[`${p.id}:case_opening`];
        const draftVal = refVolDraft[p.id] ?? (Number(p.referenceVolume || 0) || "");
        const savedVal = Number(p.referenceVolume || 0);
        const rvBusy = !!refVolBusy[p.id];
        const playerRefs = refs.filter((r) => r.playerId === p.id);
        const active = playerRefs.filter((r) => ["active", "closing"].includes(r.status));
        const past   = playerRefs.filter((r) => ["completed", "terminated"].includes(r.status));
        const nameOf = (arr, id, key) => (arr.find((x) => x.id === id) || {})[key] || "—";
        const proofFiles = Array.isArray(p.proofFiles) ? p.proofFiles : [];
        const renderContracts = (list, key, emptyLabel) => (
          <table className="w-full text-[11px] font-mono border-b border-white/5" data-testid={`player-detail-contracts-${key}`}>
            <thead className="text-[9px] uppercase tracking-widest text-[#8B97AE]"><tr>
              <th className="text-left p-1">Streamer</th><th className="text-left p-1">Vertical</th><th className="text-left p-1">Operator</th>
              <th className="text-left p-1">Signup</th><th className="text-left p-1">End</th>
              <th className="text-left p-1">ACV</th><th className="text-left p-1">Tracked</th><th className="text-left p-1">Status</th>
            </tr></thead>
            <tbody>
              {list.length === 0 && <tr><td className="p-2 text-[#8B97AE]" colSpan={8} data-testid={`player-detail-contracts-empty-${key}`}>— {emptyLabel} —</td></tr>}
              {list.map((r) => {
                const endDate = r.status === "completed" ? (r.completedAt || r.closedAt || "") : r.status === "terminated" ? (r.terminatedAt || (r.termination || {}).at || "") : "";
                return (
                  <tr key={r.id} className="border-t border-white/5" data-testid={`player-detail-contract-${r.id}`}>
                    <td className="p-1">{nameOf(streamers, r.streamerId, "displayName")}</td>
                    <td className="p-1">{r.vertical || "casino"}</td>
                    <td className="p-1">{nameOf(casinos, r.casinoId, "name")}</td>
                    <td className="p-1">{(r.signupDate || r.createdAt || "").slice(0, 10)}</td>
                    <td className="p-1">{endDate ? endDate.slice(0, 10) : "—"}</td>
                    <td className="p-1">{r.committedVolume || 0}</td>
                    <td className="p-1">{r.underCodeVolumeCumulative || 0}</td>
                    <td className="p-1"><Pill tone={(r.status === "completed" || r.status === "terminated") ? "ok" : "warn"}>{r.status}</Pill></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
        return (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="admin-player-detail-modal" onClick={closePlayerDetail}>
            <div onClick={(e) => e.stopPropagation()} className="bg-[#0B0E14] border border-white/15 max-w-3xl w-full p-6 space-y-4 mt-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">Player detail</div>
                  <div className="font-display text-2xl font-black tracking-tighter flex items-baseline gap-2 flex-wrap" data-testid="player-detail-alias">
                    <span>{p.alias}</span>
                    {p.pseudonym && (
                      <span
                        className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] font-normal"
                        data-testid="player-detail-pseudonym"
                        title="Stable pseudonym shown to pre-unlock streamers. Use to match chat/support references."
                      >{p.pseudonym}</span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="player-detail-email">{p.email} · email-verified: {String(!!p.emailVerified)}</div>
                </div>
                <button data-testid="player-detail-close" onClick={closePlayerDetail} className="text-[#8B97AE] hover:text-white text-sm font-mono">close</button>
              </div>

              {/* Status derived from per-vertical (iter 47 — single source of
                  truth). The Verification Tier dropdown was removed; the
                  `verificationTier` field is retained on the doc so the VIP
                  badge in the marketplace + the legacy casino fallback keep
                  working until a one-shot migration retires them. */}
              {(() => {
                const _vv = p.verticalVerification || {};
                const casinoSet = Object.prototype.hasOwnProperty.call(_vv, "casino");
                const caseSet   = Object.prototype.hasOwnProperty.call(_vv, "case_opening");
                const legacyCasinoVerified = !casinoSet && ["verified", "vip"].includes(p.verificationTier) && p.status === "approved";
                const casinoDerived = casinoSet ? (_vv.casino ? "verified" : "declined") : (legacyCasinoVerified ? "verified" : "pending");
                const caseDerived   = caseSet ? (_vv.case_opening ? "verified" : "declined") : "pending";
                const overall = ([casinoDerived, caseDerived].includes("verified"))
                  ? "verified"
                  : ([casinoDerived, caseDerived].every((s) => s === "declined")) ? "declined" : "pending";
                const tone = overall === "verified" ? "ok" : overall === "declined" ? "danger" : "warn";
                return (
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Verification (derived from per-vertical)</span>
                      <div className="mt-1 flex items-center gap-2" data-testid="player-detail-status">
                        <Pill tone={tone}>{overall}</Pill>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
                          casino: {casinoDerived} · case-pack: {caseDerived}
                        </span>
                      </div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] mt-1" data-testid="player-detail-account-state">
                        account state: {p.status} · onboarding: {p.onboardingComplete ? "complete" : "incomplete"}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Per-vertical explicit verification — iter 45 tri-state
                  (Verify / Decline / Reset). Reset returns to pending, which
                  for Casino falls back to the legacy tier inference (marked
                  "LEGACY VERIFIED" pill); for Case-Pack pending == excluded
                  from the marketplace (no legacy path). */}
              {(() => {
                const stateOf = (vertical) => {
                  if (Object.prototype.hasOwnProperty.call(vv, vertical)) {
                    return vv[vertical] === true ? "verified" : "declined";
                  }
                  if (vertical === "casino" && ["verified", "vip"].includes(p.verificationTier) && p.status === "approved") {
                    return "legacy_verified";
                  }
                  return "pending";
                };
                const stateTone = (s) => s === "verified" || s === "legacy_verified" ? "ok" : s === "declined" ? "danger" : "default";
                const stateLabel = (s) => ({ verified: "VERIFIED", legacy_verified: "VERIFIED (legacy)", declined: "DECLINED", pending: "PENDING" }[s]);
                const casinoState = stateOf("casino");
                const caseState   = stateOf("case_opening");
                const renderRow = (vertical, label, current, busy) => (
                  <div className="flex flex-wrap items-center gap-3" data-testid={`player-detail-pvv-row-${vertical}`}>
                    <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE] w-24">{label}</div>
                    <Pill tone={stateTone(current)}>
                      <span data-testid={`player-detail-${vertical}-state-${p.id}`}>{stateLabel(current)}</span>
                    </Pill>
                    <div className="flex items-center gap-2 ml-auto">
                      <button
                        data-testid={`player-detail-${vertical}-verify-${p.id}`}
                        disabled={busy || current === "verified"}
                        onClick={() => setPlayerVerticalState(p.id, vertical, "verified")}
                        className="px-2 py-1 border border-[#2DD4A7]/50 text-[#2DD4A7] hover:bg-[#2DD4A7]/10 text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-30"
                      >{busy ? "…" : "Verify"}</button>
                      <button
                        data-testid={`player-detail-${vertical}-decline-${p.id}`}
                        disabled={busy || current === "declined"}
                        onClick={() => setPlayerVerticalState(p.id, vertical, "declined")}
                        className="px-2 py-1 border border-[#F26D6D]/50 text-[#F26D6D] hover:bg-[#F26D6D]/10 text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-30"
                      >{busy ? "…" : "Decline"}</button>
                      <button
                        data-testid={`player-detail-${vertical}-reset-${p.id}`}
                        disabled={busy || current === "pending"}
                        onClick={() => setPlayerVerticalState(p.id, vertical, "pending")}
                        className="px-2 py-1 border border-white/25 text-[#8B97AE] hover:text-white hover:border-white text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-30"
                      >{busy ? "…" : "Reset"}</button>
                    </div>
                  </div>
                );
                return (
                  <div className="border border-white/10 p-4 space-y-3" data-testid="player-detail-pvv">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Per-vertical verification (tri-state · manual · audit-logged)</div>
                      <button data-testid={`player-detail-pvv-history-${p.id}`} onClick={() => openPvvHistory(p)} className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#2DD4A7] transition-colors">History</button>
                    </div>
                    {renderRow("casino", "Casino", casinoState, casinoBusy)}
                    {renderRow("case_opening", "Case-Pack", caseState, caseBusy)}
                    <div className="text-[10px] font-mono text-[#8B97AE] leading-relaxed">
                      Verify → grants marketplace eligibility in this vertical. Decline → explicit rejection, excluded from the marketplace. Reset → returns to pending (Casino falls back to the legacy tier inference until an explicit decision).
                    </div>
                  </div>
                );
              })()}

              {/* PII (admin-only). This block only renders because the request
                  was made by an authenticated admin — GET /players/{id} is 403
                  for streamers by design. */}
              <div className="border border-[#F26D6D]/30 bg-[#F26D6D]/5 p-4 space-y-2" data-testid="player-detail-pii">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">PII · admin-only · never on the marketplace</div>
                <div className="grid grid-cols-2 gap-3 text-xs font-mono">
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE] block">Casino gaming account</span><span data-testid="player-detail-gaming-name">{p.gamingAccountName || "—"}</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE] block">Case gaming account</span><span data-testid="player-detail-case-gaming-name">{p.caseGamingAccountName || "—"}</span></div>
                  <div className="col-span-2"><span className="text-[10px] uppercase tracking-widest text-[#8B97AE] block">Wallet address (public key only)</span><span data-testid="player-detail-wallet" className="break-all">{p.cryptoWallet || "—"}</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE] block">Coin / network</span><span data-testid="player-detail-wallet-net">{(p.walletCoin || "—")} / {(p.walletNetwork || "—")}</span></div>
                  <div><span className="text-[10px] uppercase tracking-widest text-[#8B97AE] block">XRP tag</span><span data-testid="player-detail-wallet-tag">{p.walletTag || "—"}</span></div>
                </div>
              </div>

              {/* Iter 139 — Offered volume (self-reported · pricing basis). READ-ONLY.
                  Placed ABOVE the admin-verified block on purpose: this is the ONLY
                  self-reported figure that enters commission + chat-unlock math
                  (Iter 70). Per vertical. NEVER summed. Sourced verbatim from
                  `p.offeredVolumeByVertical.<vertical>` — the field the player
                  edits via PATCH /api/players/me/offered-volume with the 50–200%
                  bound (see OfferedVolumeEditor.jsx). Weighted reference: casino
                  = weightedCasino(p), case = p.caseWagerSelfReported (coef 1.0).
                  Admins MUST NOT edit this here — doing so would be a pricing-
                  manipulation vector. Display only. */}
              <div className="border border-[#F5B841]/40 bg-[#F5B841]/5 p-4 space-y-2" data-testid="player-detail-offered-volume">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">
                  Offered volume (self-reported · for 90 days)
                </div>
                <div className="text-[10px] font-mono text-[#8B97AE]">
                  Only self-reported figure that enters commission + chat-unlock.
                </div>
                {[
                  { vertical: "casino",       label: "Casino", weighted: weightedCasino(p) || 0 },
                  { vertical: "case_opening", label: "Case",   weighted: Number(p.caseWagerSelfReported || 0) },
                ].map(({ vertical, label, weighted }) => {
                  const raw = (p.offeredVolumeByVertical || {})[vertical];
                  const offered = raw == null ? null : Number(raw);
                  const isSet = offered != null && offered > 0;
                  const pct = weighted > 0 && isSet ? Math.round((offered / weighted) * 100) : null;
                  return (
                    <div
                      key={vertical}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[#F5B841]/20 pt-2"
                      data-testid={`player-detail-offered-row-${vertical}`}
                    >
                      <span className="text-[11px] font-mono uppercase tracking-widest text-[#F5B841] min-w-[56px]">{label}</span>
                      <span className="text-[10px] font-mono text-[#8B97AE]">Offered:</span>
                      {isSet ? (
                        <span
                          data-testid={`player-detail-offered-amount-${vertical}`}
                          className="tabular-nums text-[#E8ECF4] font-mono"
                          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                        >${fmtInt(offered)}</span>
                      ) : (
                        <span
                          data-testid={`player-detail-offered-amount-${vertical}`}
                          className="text-[11px] font-mono text-[#8B97AE]"
                        >— not set</span>
                      )}
                      <span className="text-[10px] font-mono text-[#8B97AE]">·</span>
                      <span
                        data-testid={`player-detail-offered-pct-${vertical}`}
                        className="text-[10px] font-mono tabular-nums text-[#F5B841]"
                      >{isSet && weighted > 0 ? `${pct}%` : "—%"}</span>
                      <span className="text-[10px] font-mono text-[#8B97AE]">of weighted</span>
                      <span
                        className="tabular-nums text-[#E8ECF4] text-[11px] font-mono"
                        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      >${fmtInt(weighted)}</span>
                      <span className="text-[10px] font-mono text-[#8B97AE]">·</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">bound 50–200%</span>
                    </div>
                  );
                })}
              </div>

              {/* Iter 124 — Per-category admin verification. Reads self-
                  reported categories as pre-fill; admin edits/confirms;
                  server computes weighted = Σ(cat × onboarding coefficient)
                  and persists both `verifiedReferenceCategories.<v>` +
                  `verifiedReferenceWeighted.<v>`. NEVER read by
                  pricing/reserve/bounds/commission/rebate (Iter 70 already
                  migrated those to offeredVolumeByVertical). */}
              {/* Iter 195 — Admin-verified volume panel is now DISPLAY-ONLY.
                  Single source of truth for period/mode is the player's
                  onboarding submission (server.py:9501). Admin's only actions
                  are Confirm (approve — gates marketplace visibility) or the
                  existing status controls above. Removing the input fields
                  eliminates the class of bug where admin's typed values could
                  drift out of sync with what the player actually submitted. */}
              <div className="border border-white/10 p-4 space-y-3" data-testid="player-detail-refvol">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Admin-verified volume · <span className="text-[#2DD4A7]">display-only (as submitted by player)</span> · info only · gates marketplace visibility — <span className="text-[#F5B841]">NEVER used in pricing / commission / rebate</span></div>
                {[
                  { vertical: "casino", label: "Casino", coeffs: (wagerCoeffCfg?.categories || []).map((c) => ({ key: c.key, coefficient: Number(c.coefficient || 0) })) },
                  { vertical: "case_opening", label: "Case", coeffs: [{ key: "case_opening", coefficient: 1.0 }] },
                ].map(({ vertical, label, coeffs }) => {
                  if (vertical === "casino" && coeffs.length === 0) return null;
                  const savedWeighted = Number(((p.verifiedReferenceWeighted || {})[vertical]) || 0);
                  const legacyWeighted = Number(((p.referenceVolumeByVertical || {})[vertical]) || (vertical === "casino" ? (p.referenceVolume || 0) : 0));
                  const draftKey = `${p.id}:${vertical}`;
                  const busy = !!refVolBusy[draftKey];
                  // Iter 195 — Single source of truth: the player's onboarding submission.
                  const playerPeriod = Number(p.wagerReportPeriodDays || 90);
                  const playerMode   = p.wagerReportMode || "categorized";
                  const casinoCats   = p.wagerCategoriesSelfReported || {};
                  const casinoTotal  = Number(p.wagerTotalSelfReported || 0);
                  const caseWager    = Number(p.caseWagerSelfReported || 0);
                  // Values to display + values to POST on confirm — always the player's.
                  const rowValue = (k) => vertical === "casino"
                    ? Number(casinoCats[k] || 0)
                    : (k === "case_opening" ? caseWager : 0);
                  const submittedWeighted = vertical === "casino"
                    ? Math.round(weightedFor("casino", p, wagerCoeffCfg))
                    : Math.round(weightedFor("case_opening", p, wagerCoeffCfg));
                  const hasSubmittedData = vertical === "casino"
                    ? (playerMode === "total_only" ? casinoTotal > 0 : coeffs.some((c) => Number(casinoCats[c.key] || 0) > 0))
                    : caseWager > 0;
                  return (
                    <div key={vertical} className="border-t border-white/5 pt-3" data-testid={`player-detail-refvol-row-${vertical}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[11px] font-mono uppercase tracking-widest text-[#F5B841]">{label}</span>
                        <span className="text-[10px] font-mono text-[#8B97AE]">submitted weighted: <span className="text-[#2DD4A7] tabular-nums">{submittedWeighted.toLocaleString()}</span></span>
                        {savedWeighted > 0
                          ? <span className="text-[10px] font-mono text-[#8B97AE]" data-testid={`player-detail-refvol-saved-${p.id}-${vertical}`}>confirmed: {savedWeighted.toLocaleString()}</span>
                          : legacyWeighted > 0
                            ? <span className="text-[10px] font-mono text-[#8B97AE]">legacy: {legacyWeighted.toLocaleString()}</span>
                            : <span className="text-[10px] font-mono text-[#F5B841]">unconfirmed · no {label} card</span>}
                      </div>
                      <div
                        className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2 flex flex-wrap gap-2 items-center"
                        data-testid={`refvol-as-submitted-${vertical}`}
                      >
                        <span>As submitted by player ·</span>
                        <span className="text-[#2DD4A7] border border-[#2DD4A7]/40 px-1.5 py-0.5" data-testid={`refvol-shown-period-${vertical}`}>{playerPeriod}-day window</span>
                        <span className="text-[#E8ECF4] border border-white/15 px-1.5 py-0.5" data-testid={`refvol-shown-mode-${vertical}`}>{vertical === "case_opening" ? "single wager" : (playerMode === "total_only" ? "single total" : "per category")}</span>
                      </div>
                      {hasSubmittedData ? (
                        (vertical === "casino" && playerMode === "total_only") ? (
                          <div className="border border-white/10 p-2 mb-2 flex items-baseline justify-between" data-testid={`refvol-readonly-total-${vertical}`}>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">total <span className="text-[8px] text-[#8B97AE]/60">× 0.45</span></span>
                            <span className="tabular-nums text-[#E8ECF4] font-mono text-xs">${casinoTotal.toLocaleString()}</span>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                            {coeffs.map((c) => (
                              <div key={c.key} className="border border-white/10 p-2 flex flex-col" data-testid={`refvol-readonly-cat-${p.id}-${vertical}-${c.key}`}>
                                <span className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">{c.key.replace(/_/g, " ")} <span className="text-[8px] text-[#8B97AE]/60">× {c.coefficient}</span></span>
                                <span className="tabular-nums text-[#E8ECF4] font-mono text-xs mt-0.5">${rowValue(c.key).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )
                      ) : (
                        <div className="text-[11px] font-mono text-[#8B97AE] py-2" data-testid={`refvol-readonly-empty-${vertical}`}>— player has not submitted a {label.toLowerCase()} wager report —</div>
                      )}
                      <button
                        data-testid={`player-detail-refvol-save-${p.id}-${vertical}`}
                        onClick={async () => {
                          if (!hasSubmittedData) { toast.error("Player has not submitted a wager report for this vertical."); return; }
                          setRefVolBusy((old) => ({ ...old, [draftKey]: true }));
                          try {
                            // Iter 195 — Confirm posts the PLAYER's own submitted
                            // values back to the endpoint. Admin never retypes
                            // the period or numbers. Case vertical always sends
                            // the single case wager under `case_opening`.
                            const body = {
                              vertical,
                              periodDays: playerPeriod,
                              mode: vertical === "case_opening" ? "categorized" : playerMode,
                            };
                            if (vertical === "case_opening") {
                              body.categories = { case_opening: caseWager };
                            } else if (playerMode === "total_only") {
                              body.categories = Object.fromEntries(coeffs.map((c) => [c.key, 0]));
                              body.total = casinoTotal;
                            } else {
                              body.categories = Object.fromEntries(coeffs.map((c) => [c.key, Number(casinoCats[c.key] || 0)]));
                            }
                            const r = await api.post(`/players/${p.id}/verified-reference`, body);
                            toast.success(`Verified · weighted ${Number(r.data.verifiedReferenceWeighted).toLocaleString()}`);
                            await load();
                          } catch (e) { toast.error(apiErrorMessage(e)); }
                          finally { setRefVolBusy((old) => ({ ...old, [draftKey]: false })); }
                        }}
                        disabled={busy || !hasSubmittedData}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-[#E8ECF4] transition-colors disabled:opacity-40 border border-[#2DD4A7]/40 px-3 py-1.5"
                      >{busy ? "confirming…" : "confirm verified"}</button>
                    </div>
                  );
                })}
              </div>

              {/* Iter 44 — Self-reported onboarding breakdown (Casino + Case).
                  DISPLAY-ONLY. Values pulled from the already-stored onboarding
                  fields (wagerCategoriesSelfReported, caseWagerSelfReported,
                  currentCaseOperator). Nothing here is persisted or fed into
                  billing/rebate/pricing. Baustein-3 admin-confirmed weighted
                  monthly base is a separate control (Reference Volume above)
                  and is intentionally NOT merged with these blocks. */}
              {(() => {
                const cats = wagerCoeffCfg?.categories || [];
                const amounts = p.wagerCategoriesSelfReported || {};
                const CAT_LABELS = {
                  slots: "Slots",
                  house_originals: "House / Originals",
                  table_games: "Table Games",
                  live_casino: "Live Casino",
                  sports: "Sports",
                  dice: "Dice",
                };
                // Iter 194 — mode-aware self-reported casino display. When the
                // player is on total_only reporting, render the single stored
                // total + "×0,45" origin marker instead of the six zeroed
                // category rows (which is what the pre-fix build showed).
                const wagerMode = p.wagerReportMode || "categorized";
                const totalCasino = Number(p.wagerTotalSelfReported || 0);
                const hasCasino = wagerMode === "total_only"
                  ? totalCasino > 0
                  : cats.some((c) => Number(amounts[c.key] || 0) > 0);
                const wCasino = weightedCasino(p) || 0;
                const caseAmt = Number(p.caseWagerSelfReported || 0);
                const caseOp  = p.currentCaseOperator || {};
                const caseOpLabel = caseOp.otherName || caseOp.caseOperatorId || "";
                const hasCase = caseAmt > 0 || !!caseOpLabel;
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="player-detail-selfreport">
                    {/* Block 1 — Casino */}
                    <div className="border border-[#2DD4A7]/30 bg-[#2DD4A7]/5 p-4 space-y-2" data-testid="player-detail-selfreport-casino">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">Casino (self-reported)</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">est. · never billed</div>
                      </div>
                      {hasCasino ? (
                        <>
                          {wagerMode === "total_only" ? (
                            <div className="flex items-baseline justify-between border-b border-white/5 py-1" data-testid="player-detail-cat-total">
                              <span className="text-[#8B97AE] text-[11px]">Total · {t("card.wager.originLump")}</span>
                              <span
                                className="tabular-nums text-[#E8ECF4]"
                                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                              >${fmtInt(totalCasino)}</span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              {cats.map((c) => {
                                const amt = Number(amounts[c.key] || 0);
                                return (
                                  <div key={c.key} className="flex items-baseline justify-between border-b border-white/5 py-1" data-testid={`player-detail-cat-${c.key}`}>
                                    <span className="text-[#8B97AE] text-[11px]">{CAT_LABELS[c.key] || c.key}</span>
                                    <span className="tabular-nums text-[#E8ECF4]" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>${fmtInt(amt)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <div className="pt-2 flex items-baseline justify-between border-t border-[#2DD4A7]/30">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">
                              {wagerMode === "total_only"
                                ? `Weighted (${t("card.wager.originLump")})`
                                : "Weighted (Σ × coeff)"}
                            </span>
                            <span
                              data-testid="player-detail-casino-weighted"
                              className="text-lg font-bold tabular-nums text-[#E8ECF4]"
                              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                            >${fmtInt(wCasino)}</span>
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">self-reported · never used in billing</div>
                        </>
                      ) : (
                        <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-selfreport-casino-empty">— no self-reported casino wager —</div>
                      )}
                    </div>

                    {/* Block 2 — Case sites */}
                    <div className="border border-[#F5B841]/30 bg-[#F5B841]/5 p-4 space-y-2" data-testid="player-detail-selfreport-case">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Case sites (self-reported)</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE]">est. · never billed</div>
                      </div>
                      {hasCase ? (
                        <>
                          <div className="flex items-baseline justify-between border-b border-white/5 py-1">
                            <span className="text-[#8B97AE] text-[11px]">Operator</span>
                            <span data-testid="player-detail-case-operator" className="text-[#E8ECF4] text-xs font-mono">{caseOpLabel || "—"}</span>
                          </div>
                          <div className="flex items-baseline justify-between border-b border-white/5 py-1">
                            <span className="text-[#8B97AE] text-[11px]">Wager</span>
                            <span
                              data-testid="player-detail-case-wager"
                              className="tabular-nums text-[#E8ECF4]"
                              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                            >${fmtInt(caseAmt)}</span>
                          </div>
                          <div className="pt-2 flex items-baseline justify-between border-t border-[#F5B841]/30">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">Weighted (coef 1.0)</span>
                            <span
                              data-testid="player-detail-case-weighted"
                              className="text-lg font-bold tabular-nums text-[#E8ECF4]"
                              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                            >${fmtInt(caseAmt)}</span>
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">self-reported · never used in billing</div>
                        </>
                      ) : (
                        <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-selfreport-case-empty">— no self-reported case wager —</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Iter 192 — Request More Wager Information block.
                  Positioned directly above "Contracts (this player)" per
                  spec. Sends the player back into onboarding (Step 2) with
                  a banner showing the admin's message. Distinct player
                  status `needs_more_info` — separate from the existing
                  wager-reminder cadence (kept intact below). */}
              {(() => {
                const canRequest = p.onboardingComplete === true;
                const hist = Array.isArray(p.moreInfoRequests) ? p.moreInfoRequests : [];
                const sortedHist = hist
                  .filter((h) => h && h.sent_at)
                  .slice()
                  .sort((a, b) => String(a.sent_at).localeCompare(String(b.sent_at)));
                const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_) { return iso; } };
                const msg = moreInfoMsg[p.id] || "";
                const busy = !!moreInfoBusy[p.id];
                const canSend = canRequest && !busy && msg.trim().length >= 5;
                const send = async () => {
                  if (!canSend) return;
                  const ok = window.confirm(`Send this player back into onboarding with your message?\n\n${p.status === "needs_more_info" ? "(They are already in the 'needs more info' state — this will add another request.)" : ""}`);
                  if (!ok) return;
                  setMoreInfoBusy((s) => ({ ...s, [p.id]: true }));
                  try {
                    await api.post(`/admin/players/${p.id}/request-more-info`, { message: msg.trim() });
                    toast.success("Request sent — the player has been reopened for onboarding.");
                    setMoreInfoMsg((s) => ({ ...s, [p.id]: "" }));
                    try {
                      const fresh = await api.get(`/players/${p.id}`);
                      setPlayerDetail(fresh.data);
                    } catch (_) { /* soft-fail; toast already fired */ }
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
                  } finally {
                    setMoreInfoBusy((s) => ({ ...s, [p.id]: false }));
                  }
                };
                return (
                  <div className="border border-white/10 p-4 space-y-3" data-testid="player-detail-more-info-request">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] border border-[#F5B841]/60 px-2 py-0.5">Request more wager information</span>
                      {p.status === "needs_more_info" && (
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="player-detail-more-info-active">Currently open · awaiting player</span>
                      )}
                    </div>
                    {!canRequest ? (
                      <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-more-info-onboarding-incomplete">
                        Onboarding is not yet complete — this player can still edit freely.
                      </div>
                    ) : (
                      <>
                        <div className="text-[11px] font-mono text-[#8B97AE]">
                          Sends this player back into Step 2 onboarding with your message shown as a banner. Nothing gets erased — their previous entries and proofs are preserved. Their status flips back to <span className="text-[#E8ECF4]">pending</span> automatically when they resubmit.
                        </div>
                        <textarea
                          data-testid="player-detail-more-info-textarea"
                          value={msg}
                          onChange={(e) => setMoreInfoMsg((s) => ({ ...s, [p.id]: e.target.value }))}
                          placeholder="e.g. Please upload a clearer screenshot of your last-90-days table-games turnover — the current one is cropped."
                          rows={4}
                          maxLength={2000}
                          className="w-full bg-[#0B0E14] border border-white/15 focus:border-[#F5B841] outline-none px-3 py-2 font-mono text-[12px] text-[#E8ECF4] placeholder-[#4E5B6F] resize-y"
                        />
                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            data-testid={`player-detail-more-info-send-${p.id}`}
                            disabled={!canSend}
                            onClick={send}
                            className="rounded-none bg-[#F5B841] text-[#04150F] hover:bg-[#F5B841]/80 h-9 px-4 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
                          >{busy ? "Sending…" : "Send request"}</Button>
                          <span className="text-[10px] font-mono text-[#8B97AE]">Min 5 chars · max 2000</span>
                        </div>
                      </>
                    )}
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">History</div>
                      {sortedHist.length === 0 ? (
                        <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-more-info-empty">No requests sent yet.</div>
                      ) : (
                        <ol className="text-[11px] font-mono text-[#E8ECF4] space-y-1.5" data-testid="player-detail-more-info-history">
                          {sortedHist.map((h, i) => (
                            <li key={i} data-testid={`player-detail-more-info-entry-${i}`} className="border-l-2 border-[#F5B841]/40 pl-2">
                              <div>
                                <span className="text-[#8B97AE]">{i + 1}.</span>{" "}
                                <span className="text-[#F5B841]">{fmt(h.sent_at)}</span>
                                {h.sent_by ? <span className="text-[#8B97AE]"> · {h.sent_by}</span> : null}
                              </div>
                              <div className="text-[#E8ECF4] mt-0.5 whitespace-pre-wrap break-words">{h.message}</div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Contracts (active / past) */}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Contracts (this player)</div>
                <div className="space-y-2">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#2DD4A7]">Active</div>
                  {renderContracts(active, "active", "no active contracts")}
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B97AE] pt-1">Past</div>
                  {renderContracts(past, "past", "no past contracts")}
                </div>
              </div>

              {/* Proofs (presigned URL only — bucket is private) */}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">Proofs (presigned — bucket is private)</div>
                <ul className="space-y-2" data-testid="player-detail-proofs">
                  {proofFiles.map((f) => {
                    const k = f.path;
                    return (
                      <li key={k} className="border border-white/10 p-2" data-testid={`player-detail-proof-${k.slice(-12)}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-xs font-mono text-[#E8ECF4]">{f.filename || k.split("/").pop()}</span>
                          {!playerProofUrls[k]
                            ? <button data-testid={`player-detail-proof-view-${k.slice(-12)}`} onClick={() => fetchPlayerProofUrl(k)} className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] hover:text-white">View</button>
                            : <a href={playerProofUrls[k]} target="_blank" rel="noreferrer noopener" data-testid={`player-detail-proof-open-${k.slice(-12)}`} className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] hover:text-white">Open in new tab</a>
                          }
                        </div>
                        {playerProofUrls[k] && (
                          /\.(jpe?g|png|webp)$/i.test(k)
                            ? <img src={playerProofUrls[k]} alt="" className="mt-2 max-h-64 object-contain border border-white/10" data-testid={`player-detail-proof-img-${k.slice(-12)}`} />
                            : /\.(mp4|mov|webm)$/i.test(k)
                              ? <video src={playerProofUrls[k]} controls className="mt-2 max-h-64 w-full border border-white/10" data-testid={`player-detail-proof-vid-${k.slice(-12)}`} />
                              : <div className="mt-2 text-[10px] font-mono uppercase text-[#8B97AE]">Preview available via the "Open in new tab" link.</div>
                        )}
                      </li>
                    );
                  })}
                  {proofFiles.length === 0 && <li className="text-[11px] font-mono text-[#F26D6D]">— no proofs uploaded —</li>}
                </ul>
              </div>

              {/* Iter 180 — Wager Verification Reminders block.
                  Backend: history is an array on the player doc that
                  accumulates every automatic (cron) and manual (this
                  button) send. Shown between Proofs and the delete-row
                  because it's tied to onboarding progress, and admins
                  are already scanning that area to decide next steps. */}
              {(() => {
                const rawHist = Array.isArray(p.wager_reminder_history) ? p.wager_reminder_history : [];
                const hist = rawHist
                  .filter((h) => h && h.sent_at)
                  .slice()
                  .sort((a, b) => String(a.sent_at).localeCompare(String(b.sent_at)));
                const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_) { return iso; } };
                const onboarded = p.onboardingComplete === true;
                const busy = !!playerReminderBusy[p.id];
                const sendManual = async () => {
                  const ok = window.confirm(`Send the wager verification reminder to ${p.alias || "this player"} now?`);
                  if (!ok) return;
                  setPlayerReminderBusy((s) => ({ ...s, [p.id]: true }));
                  try {
                    const { data } = await api.post(`/admin/players/${p.id}/wager-reminder`);
                    toast.success("Reminder sent.");
                    // Reload the player doc so the new history entry appears.
                    try {
                      const fresh = await api.get(`/players/${p.id}`);
                      setPlayerDetail(fresh.data);
                    } catch (_) {
                      // Local append fallback keeps the UI honest even if the
                      // reload fails for any reason.
                      setPlayerDetail((prev) => prev ? { ...prev, wager_reminder_history: [...(prev.wager_reminder_history || []), data.entry] } : prev);
                    }
                  } catch (e) {
                    toast.error(apiErrorMessage(e));
                  } finally {
                    setPlayerReminderBusy((s) => ({ ...s, [p.id]: false }));
                  }
                };
                return (
                  <div className="border border-white/10 p-4 space-y-3" data-testid="player-detail-wager-reminder">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] border border-[#2DD4A7]/60 px-2 py-0.5">Wager verification reminders</span>
                    </div>
                    <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-registered">
                      Registered: <span className="text-[#E8ECF4]">{p.createdAt ? fmt(p.createdAt) : "—"}</span>
                    </div>
                    {onboarded ? (
                      <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-onboarded-note">
                        Onboarding is complete — no more reminders will be sent.
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          data-testid={`player-detail-send-reminder-${p.id}`}
                          disabled={busy}
                          onClick={sendManual}
                          className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#2DD4A7]/80 h-9 px-4 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
                        >{busy ? "Sending…" : "Send wager verification reminder now"}</Button>
                        <span className="text-[10px] font-mono text-[#8B97AE]">Bypasses the 3d/7d cadence · repeatable</span>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">History</div>
                      {hist.length === 0 ? (
                        <div className="text-[11px] font-mono text-[#8B97AE]" data-testid="player-detail-reminder-empty">No reminders sent yet.</div>
                      ) : (
                        <ol className="text-[11px] font-mono text-[#E8ECF4] space-y-1" data-testid="player-detail-reminder-history">
                          {hist.map((h, i) => (
                            <li key={i} data-testid={`player-detail-reminder-entry-${i}`}>
                              <span className="text-[#8B97AE]">{i + 1}.</span>{" "}
                              <span className={h.type === "manual" ? "text-[#F5B841]" : "text-[#2DD4A7]"}>
                                {h.type === "manual" ? "Manual" : "Automatic"}
                              </span>
                              {" — "}
                              {fmt(h.sent_at)}
                              {h.type === "manual" && h.sent_by ? <span className="text-[#8B97AE]">, sent by {h.sent_by}</span> : null}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Iter 47 — Approve / Reject removed. Per-vertical Verify is
                  now the single source of truth: verifying a vertical
                  auto-promotes status:pending→approved server-side. Delete
                  stays for hard removal (GDPR). */}
              <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-white/10">
                <Button data-testid={`player-detail-delete-${p.id}`} onClick={async () => { await deletePlayer(p.id); closePlayerDetail(); }} className="rounded-none bg-transparent border border-white/25 text-[#8B97AE] hover:text-white hover:border-white h-10">{t("dash.delete")}</Button>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] ml-auto">Verification is managed above (per-vertical)</span>
              </div>
            </div>
          </div>
        );
      })()}
      {playerDetailBusy && !playerDetail && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center pointer-events-none" data-testid="player-detail-loading">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">Loading player…</div>
        </div>
      )}
      {openPayoutId && (
        <PayoutDetailModal
          payoutId={openPayoutId}
          onClose={() => setOpenPayoutId(null)}
          onSaved={() => load()}
        />
      )}
      {openRecipientForPayoutId && (
        <RecipientOverviewModal
          payoutId={openRecipientForPayoutId}
          onClose={() => setOpenRecipientForPayoutId(null)}
        />
      )}
      {openContractId && (
        <AdminContractDetailModal
          contractId={openContractId}
          onClose={() => setOpenContractId(null)}
          onChanged={() => { loadIter3(); load(); }}
        />
      )}
      {creditHistoryStreamer && (
        <CreditHistoryModal
          streamer={creditHistoryStreamer}
          onClose={() => setCreditHistoryStreamer(null)}
        />
      )}
      {topupDetailOpen && (
        <TopupDetailModal
          topup={topupDetailOpen}
          streamer={streamers.find((s) => s.id === topupDetailOpen.streamerId)}
          onClose={() => setTopupDetailOpen(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}
